import React, { useState, useEffect, useMemo } from 'react';
import { AthleteProfile, PickupGame, SportType, Goal, Badge, CourtPOI, Tournament, OfflineQueueItem, Sponsor } from '../types';
import { SponsorAdBanner } from './common/SponsorAdBanner';
import { getActiveSports, getSportLabel, getSportStatSummary } from '../utils/sportUtils';
import { useSportFilter } from '../hooks/useSportFilter';
import { triggerHaptic } from '../utils/haptics';
import { AdvancedInsightsProCard } from './AdvancedInsightsProCard';
import { SmartWarmup } from './SmartWarmup';
import { QuickMatchModal } from './QuickMatchModal';
import { WeeklyChallenges } from './WeeklyChallenges';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';
import { ProTipCard } from './ProTipCard';
import { QuickStatsFloatingCard } from './QuickStatsFloatingCard';
import { RegionalPerformanceCard } from './regional/RegionalPerformanceCard';
import { LocalActivityFeed } from './regional/LocalActivityFeed';
import { TrendingLocalTournamentsCarousel } from './regional/TrendingLocalTournamentsCarousel';
import { verifyProAccess } from '../utils/authUtils';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import {
  Trophy,
  Flame,
  Award,
  MapPin,
  Users,
  CheckCircle2,
  Plus,
  PlusCircle,
  Activity,
  Zap,
  Calendar,
  ChevronRight,
  Target,
  Dumbbell,
  Sparkles,
  X,
  Clock,
  Send,
  Check,
  RotateCcw,
  Gift,
  Coins,
  ShieldCheck,
  TrendingUp,
  Share2,
  Crown,
  Lock,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Heart,
  Navigation,
  Star,
  ExternalLink,
  Trash2,
  RefreshCw
} from 'lucide-react';

interface DashboardViewProps {
  user: AthleteProfile;
  pickupGames: PickupGame[];
  tournaments?: Tournament[];
  courts?: CourtPOI[];
  favoriteCourtIds?: string[];
  selectedSport?: SportType;
  pendingQueue?: OfflineQueueItem[];
  onToggleFavoriteCourt?: (courtId: string) => void;
  onOpenStatLog: () => void;
  onOpenHostGame: () => void;
  onOpenMiniGames?: () => void;
  onJoinGame: (gameId: string) => void;
  onNavigateToTab: (tab: any) => void;
  onClaimGoalReward: (goalId: string) => void;
  onEarnXp?: (amount: number, source: string) => void;
  onUpdateProfile?: (updated: Partial<AthleteProfile>) => void;
  onOpenProCheckout?: (customContextMessage?: string) => void;
  onOpenReferralModal?: () => void;
}

// Interface for randomized daily challenges
export interface DailyChallenge {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  rewardXp: number;
  completed: boolean;
  claimed: boolean;
  icon: string;
  actionTab?: string;
}

// Interface for Weekly Challenges
export interface WeeklyChallenge {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  rewardXp: number;
  completed: boolean;
  claimed: boolean;
  icon: string;
  sportLabel?: string;
  actionTab?: string;
}

// Master pool of weekly challenge templates
const WEEKLY_CHALLENGE_TEMPLATES: Omit<WeeklyChallenge, 'current' | 'completed' | 'claimed'>[] = [
  {
    id: 'wc_make_10_shots',
    title: 'Make 10 Shots in One Session',
    description: 'Log a workout or game session with 10 or more made shots on the court.',
    target: 10,
    unit: 'shots',
    rewardXp: 250,
    icon: '🏀',
    sportLabel: 'Basketball',
    actionTab: 'analytics',
  },
  {
    id: 'wc_log_2_games',
    title: 'Log 2 Games This Week',
    description: 'Record 2 pickup or league game box scores in your profile stat logs.',
    target: 2,
    unit: 'games',
    rewardXp: 300,
    icon: '📊',
    actionTab: 'games',
  },
  {
    id: 'wc_score_20_pts',
    title: 'Score 20 Total Points',
    description: 'Accumulate 20 total points across your weekly game logs.',
    target: 20,
    unit: 'pts',
    rewardXp: 350,
    icon: '🔥',
    actionTab: 'analytics',
  },
  {
    id: 'wc_checkin_3_courts',
    title: 'Check In at 3 Local Courts',
    description: 'Locate 3 different courts on the interactive map and check in.',
    target: 3,
    unit: 'check-ins',
    rewardXp: 200,
    icon: '📍',
    actionTab: 'map',
  },
  {
    id: 'wc_dime_8_assists',
    title: 'Deliver 8 Key Assists',
    description: 'Distribute 8 setting or passing assists across your game sessions.',
    target: 8,
    unit: 'assists',
    rewardXp: 250,
    icon: '🎯',
    actionTab: 'analytics',
  },
  {
    id: 'wc_spike_5_kills',
    title: 'Log 5 Kills or Aces',
    description: 'Spike 5 kills or service aces on the net in volleyball runs.',
    target: 5,
    unit: 'kills/aces',
    rewardXp: 250,
    icon: '🏐',
    sportLabel: 'Volleyball',
    actionTab: 'games',
  },
  {
    id: 'wc_win_3_pickup',
    title: 'Win 3 Pickup Matches',
    description: 'Emerge victorious in 3 pickup matches this week.',
    target: 3,
    unit: 'wins',
    rewardXp: 400,
    icon: '🏆',
    actionTab: 'games',
  },
  {
    id: 'wc_15_kitchen_dinks',
    title: 'Execute 15 Kitchen Dinks',
    description: 'Control the kitchen line with 15 dinks in pickleball games.',
    target: 15,
    unit: 'dinks',
    rewardXp: 250,
    icon: '🏓',
    sportLabel: 'Pickleball',
    actionTab: 'games',
  },
];

const getRandomWeeklyChallenges = (): WeeklyChallenge[] => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('playground_weekly_challenges_v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 2) {
          return parsed;
        }
      } catch (e) { }
    }
  }
  const shuffled = [...WEEKLY_CHALLENGE_TEMPLATES].sort(() => 0.5 - Math.random());
  const selected = shuffled.slice(0, 2).map((tmpl) => ({
    ...tmpl,
    current: 0,
    completed: false,
    claimed: false,
  }));
  if (typeof window !== 'undefined') {
    localStorage.setItem('playground_weekly_challenges_v2', JSON.stringify(selected));
  }
  return selected;
};

// Custom Tooltip for Recharts Sparkline
const CustomSparklineTooltip = ({ active, payload, unit }: any) => {
  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    const val = payload[0].value;
    return (
      <div className="bg-indigo-950/95 border border-lime-400/50 p-3 rounded-2xl shadow-2xl backdrop-blur-md text-white text-xs space-y-1 font-sans">
        <p className="text-[10px] font-mono font-bold text-indigo-300 uppercase">{dataPoint.dateStr}</p>
        <div className="flex items-center space-x-2">
          <span className="w-2.5 h-2.5 rounded-full bg-lime-400 animate-pulse" />
          <span className="font-extrabold text-sm">{val} {unit}</span>
        </div>
        <p className="text-[10px] text-indigo-200/80 font-medium">30-Day Performance Snapshot</p>
      </div>
    );
  }
  return null;
};

export const DashboardView: React.FC<DashboardViewProps> = ({
  user,
  pickupGames,
  tournaments = [],
  courts = [],
  favoriteCourtIds = [],
  selectedSport: propSelectedSport,
  pendingQueue = [],
  onToggleFavoriteCourt,
  onOpenStatLog,
  onOpenHostGame,
  onOpenMiniGames,
  onJoinGame,
  onNavigateToTab,
  onClaimGoalReward,
  onEarnXp,
  onUpdateProfile,
  onOpenProCheckout,
  onOpenReferralModal,
}) => {
  const [selectedSport, setSelectedSport] = useState<SportType>(() => propSelectedSport || user.primarySport || 'volleyball');
  console.log("user", user)
  useEffect(() => {
    if (propSelectedSport) {
      setSelectedSport(propSelectedSport);
    }
  }, [propSelectedSport]);

  const currentSport = selectedSport || propSelectedSport || user.primarySport || 'volleyball';

  const activeSports = useMemo(() => {
    const list = getActiveSports(user);
    if (currentSport && !list.includes(currentSport)) {
      return [currentSport, ...list];
    }
    return list;
  }, [user, currentSport]);

  // Master Pool of Sport-Specific and General Daily Challenges
  const getSportChallengePool = (sport: SportType): Omit<DailyChallenge, 'current' | 'completed' | 'claimed'>[] => {
    const generalChallenges = [
      {
        id: 'ch_reach_xp',
        title: 'Reach 500 XP Milestone',
        description: 'Earn 500 total XP today through workouts, games, or social engagement.',
        target: 500,
        unit: 'XP',
        rewardXp: 150,
        icon: '⚡',
        actionTab: 'analytics' as const,
      },
      {
        id: 'ch_court_checkin',
        title: 'Check In at 2 Local Courts',
        description: 'Locate 2 courts on the live heat map and check in to show activity.',
        target: 2,
        unit: 'check-ins',
        rewardXp: 150,
        icon: '📍',
        actionTab: 'map' as const,
      },
      {
        id: 'ch_upload_highlight',
        title: 'Upload 1 Highlight Reel',
        description: 'Share a video clip or stat milestone on the social feed.',
        target: 1,
        unit: 'reel',
        rewardXp: 150,
        icon: '🎬',
        actionTab: 'highlights' as const,
      },
      {
        id: 'ch_win_runs',
        title: 'Win 2 Pickup Matches',
        description: 'Emerge victorious in at least 2 pickup games today.',
        target: 2,
        unit: 'wins',
        rewardXp: 225,
        icon: '🏆',
        actionTab: 'games' as const,
      },
    ];

    if (sport === 'volleyball') {
      return [
        {
          id: 'ch_vb_kills',
          title: 'Spike 5 Volleyball Kills',
          description: 'Log 5 or more spike kills in a volleyball match today.',
          target: 5,
          unit: 'kills',
          rewardXp: 200,
          icon: '🏐',
          actionTab: 'games' as const,
        },
        {
          id: 'ch_vb_aces',
          title: 'Serve 3 Unreturnable Aces',
          description: 'Deliver 3 service aces on the net in volleyball runs.',
          target: 3,
          unit: 'aces',
          rewardXp: 175,
          icon: '⚡',
        },
        {
          id: 'ch_vb_digs',
          title: 'Log 10 Ground Digs',
          description: 'Save 10 opponent attacks with backrow digs.',
          target: 10,
          unit: 'digs',
          rewardXp: 150,
          icon: '🛡️',
        },
        {
          id: 'ch_vb_workout',
          title: 'Log 45-Min Volleyball Drill',
          description: 'Complete a setting, spiking, or vertical jump session.',
          target: 45,
          unit: 'mins',
          rewardXp: 125,
          icon: '🏐',
        },
        ...generalChallenges,
      ];
    } else if (sport === 'soccer') {
      return [
        {
          id: 'ch_soc_goals',
          title: 'Score 2 Soccer Goals',
          description: 'Find the net with 2 goals in today\'s match.',
          target: 2,
          unit: 'goals',
          rewardXp: 200,
          icon: '⚽',
        },
        {
          id: 'ch_soc_assists',
          title: 'Deliver 2 Key Assists',
          description: 'Set up teammates with 2 goal assists.',
          target: 2,
          unit: 'assists',
          rewardXp: 175,
          icon: '🎯',
        },
        {
          id: 'ch_soc_workout',
          title: 'Log 45-Min Soccer Conditioning',
          description: 'Complete a passing, dribbling, or agility sprint session.',
          target: 45,
          unit: 'mins',
          rewardXp: 125,
          icon: '⚽',
        },
        ...generalChallenges,
      ];
    } else if (sport === 'pickleball') {
      return [
        {
          id: 'ch_pb_dinks',
          title: 'Execute 15 Kitchen Dinks',
          description: 'Control the kitchen line with 15 dink rallies.',
          target: 15,
          unit: 'dinks',
          rewardXp: 175,
          icon: '🏓',
        },
        {
          id: 'ch_pb_aces',
          title: 'Serve 3 Pickleball Aces',
          description: 'Score 3 unreturnable service aces.',
          target: 3,
          unit: 'aces',
          rewardXp: 150,
          icon: '⚡',
        },
        ...generalChallenges,
      ];
    } else if (sport === 'baseball' || sport === 'softball') {
      return [
        {
          id: 'ch_base_hits',
          title: 'Log 3 Base Hits',
          description: 'Connect for 3 base hits in your game stat log.',
          target: 3,
          unit: 'hits',
          rewardXp: 200,
          icon: '⚾',
        },
        {
          id: 'ch_base_rbis',
          title: 'Drive in 2 RBIs',
          description: 'Bring runners home with 2 RBIs.',
          target: 2,
          unit: 'RBIs',
          rewardXp: 175,
          icon: '🔥',
        },
        ...generalChallenges,
      ];
    } else if (sport === 'football') {
      return [
        {
          id: 'ch_fb_touchdowns',
          title: 'Score 2 Touchdowns',
          description: 'Reach the endzone with 2 passing or rushing TDs.',
          target: 2,
          unit: 'TDs',
          rewardXp: 200,
          icon: '🏈',
        },
        ...generalChallenges,
      ];
    } else if (sport === 'tennis') {
      return [
        {
          id: 'ch_tn_aces',
          title: 'Serve 4 Tennis Aces',
          description: 'Fire 4 unreturnable aces across the net.',
          target: 4,
          unit: 'aces',
          rewardXp: 200,
          icon: '🎾',
        },
        ...generalChallenges,
      ];
    }

    // Default Basketball
    return [
      {
        id: 'ch_bball_pts',
        title: 'Score 15 Points in Pickup',
        description: 'Log 15 or more total points on the basketball court.',
        target: 15,
        unit: 'pts',
        rewardXp: 200,
        icon: '🏀',
        actionTab: 'games' as const,
      },
      {
        id: 'ch_bball_ast',
        title: 'Dime 5 Assists',
        description: 'Distribute 5 playmaking assists to teammates.',
        target: 5,
        unit: 'assists',
        rewardXp: 175,
        icon: '🎯',
      },
      {
        id: 'ch_bball_reb',
        title: 'Grab 8 Rebounds',
        description: 'Clean the glass with 8 total offensive & defensive boards.',
        target: 8,
        unit: 'rebounds',
        rewardXp: 150,
        icon: '💪',
      },
      {
        id: 'ch_workout_45m',
        title: 'Log 45-Min Workout',
        description: 'Complete a shooting, strength, or speed conditioning session.',
        target: 45,
        unit: 'mins',
        rewardXp: 125,
        icon: '🏋️',
      },
      ...generalChallenges,
    ];
  };

  // Helper to pick 3 random sport-specific challenges from pool
  const getRandomChallenges = (activeSport?: SportType): DailyChallenge[] => {
    const targetSport = activeSport || user.primarySport || 'volleyball';
    const pool = getSportChallengePool(targetSport);
    const shuffled = [...pool].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3).map((item) => ({
      ...item,
      current: item.id === 'ch_reach_xp' ? Math.min(500, user.xp ?? 0) : 0,
      completed: false,
      claimed: false,
    }));
  };

  // State for Daily Challenges
  const [dailyChallenges, setDailyChallenges] = useState<DailyChallenge[]>(() => getRandomChallenges(user.primarySport));
  const [isShuffling, setIsShuffling] = useState(false);
  const [rewardToast, setRewardToast] = useState<{ show: boolean; title: string; xp: number } | null>(null);
  const [isChestClaimed, setIsChestClaimed] = useState(false);
  const [isQuickMatchOpen, setIsQuickMatchOpen] = useState(false);
  const [quickMatchSuccessToast, setQuickMatchSuccessToast] = useState<string | null>(null);

  // Update challenges when active sport changes
  useEffect(() => {
    setDailyChallenges(getRandomChallenges(currentSport));
  }, [currentSport]);

  // Reroll / Shuffle Challenges
  const handleRerollChallenges = () => {
    setIsShuffling(true);
    setTimeout(() => {
      setDailyChallenges(getRandomChallenges(currentSport));
      setIsShuffling(false);
    }, 400);
  };

  // Handle Progress Increment
  const handleProgressChallenge = (id: string) => {
    setDailyChallenges((prev) =>
      prev.map((ch) => {
        if (ch.id === id) {
          const nextCurrent = Math.min(ch.target, ch.current + 1);
          const isFinished = nextCurrent >= ch.target;
          return {
            ...ch,
            current: nextCurrent,
            completed: isFinished,
          };
        }
        return ch;
      })
    );
  };

  // Handle Claim Reward
  const handleClaimChallengeReward = (id: string) => {
    setDailyChallenges((prev) =>
      prev.map((ch) => {
        if (ch.id === id) {
          if (!ch.claimed) {
            // Trigger XP
            if (onEarnXp) {
              onEarnXp(ch.rewardXp, `Daily Challenge: ${ch.title}`);
            } else {
              onClaimGoalReward(id);
            }

            // Show Toast
            setRewardToast({
              show: true,
              title: ch.title,
              xp: ch.rewardXp,
            });

            setTimeout(() => {
              setRewardToast(null);
            }, 3500);
          }

          return {
            ...ch,
            current: ch.target,
            completed: true,
            claimed: true,
          };
        }
        return ch;
      })
    );
  };

  // Claim Daily Chest (+300 XP)
  const handleClaimDailyChest = () => {
    if (isChestClaimed) return;
    triggerHaptic('success');
    if (onEarnXp) {
      onEarnXp(300, 'Daily Gold Chest Bonus (+300 XP & Streak Guard)');
    }
    setIsChestClaimed(true);
    setRewardToast({
      show: true,
      title: '🎁 DAILY GOLD CHEST UNLOCKED!',
      xp: 300,
    });
    setTimeout(() => setRewardToast(null), 4000);
  };

  // State & Handlers for Weekly Challenges (displays 2 random achievable milestones)
  const [weeklyChallenges, setWeeklyChallenges] = useState<WeeklyChallenge[]>(getRandomWeeklyChallenges);
  const [isShufflingWeekly, setIsShufflingWeekly] = useState(false);

  const handleRerollWeeklyChallenges = () => {
    triggerHaptic('medium');
    setIsShufflingWeekly(true);
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('playground_weekly_challenges_v2');
      }
      const newWeekly = getRandomWeeklyChallenges();
      setWeeklyChallenges(newWeekly);
      setIsShufflingWeekly(false);
    }, 400);
  };

  const handleProgressWeeklyChallenge = (id: string) => {
    triggerHaptic('light');
    setWeeklyChallenges((prev) => {
      const updated = prev.map((ch) => {
        if (ch.id === id) {
          const nextCurrent = Math.min(ch.target, ch.current + 1);
          const isFinished = nextCurrent >= ch.target;
          if (isFinished && !ch.completed) {
            triggerHaptic('heavy');
          }
          return {
            ...ch,
            current: nextCurrent,
            completed: isFinished,
          };
        }
        return ch;
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem('playground_weekly_challenges_v2', JSON.stringify(updated));
      }
      return updated;
    });
  };

  const handleClaimWeeklyChallengeReward = (id: string) => {
    triggerHaptic('success');
    setWeeklyChallenges((prev) => {
      const updated = prev.map((ch) => {
        if (ch.id === id && !ch.claimed) {
          if (onEarnXp) {
            onEarnXp(ch.rewardXp, `Weekly Challenge: ${ch.title}`);
          } else {
            onClaimGoalReward(id);
          }
          setRewardToast({
            show: true,
            title: `Weekly Milestone Completed: ${ch.title}`,
            xp: ch.rewardXp,
          });
          setTimeout(() => setRewardToast(null), 3500);
          return { ...ch, claimed: true, completed: true };
        }
        return ch;
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem('playground_weekly_challenges_v2', JSON.stringify(updated));
      }
      return updated;
    });
  };

  // Daily Check-In Reward System Logic
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const isCheckedInToday = user.lastDailyCheckInDate === todayStr || user.checkInHistory?.includes(todayStr);

  const handleDailyCheckIn = () => {
    if (isCheckedInToday) return;

    triggerHaptic('success');
    const xpBonus = 100;

    let newStreak = user.dailyStreak || 1;
    if (user.lastDailyCheckInDate && user.lastDailyCheckInDate !== todayStr) {
      const lastTime = new Date(`${user.lastDailyCheckInDate.split('T')[0]}T00:00:00Z`).getTime();
      const todayTime = new Date(`${todayStr}T00:00:00Z`).getTime();
      const diffDays = Math.round((todayTime - lastTime) / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        newStreak += 1;
      } else if (diffDays > 1) {
        newStreak = 1;
      }
    } else if (!user.lastDailyCheckInDate) {
      newStreak = Math.max(1, user.dailyStreak || 1);
    }

    if (onEarnXp) {
      onEarnXp(xpBonus, 'Daily App Check-In Bonus');
    }

    const currentCheckIns = user.checkInHistory || [];
    const newCheckInHistory = currentCheckIns.includes(todayStr)
      ? currentCheckIns
      : [todayStr, ...currentCheckIns];

    const newLongest = Math.max(user.longestStreak || 0, newStreak);

    if (onUpdateProfile) {
      onUpdateProfile({
        lastDailyCheckInDate: todayStr,
        lastLoginDate: todayStr,
        dailyStreak: newStreak,
        longestStreak: newLongest,
        checkInHistory: newCheckInHistory,
      });
    }

    setRewardToast({
      show: true,
      title: '⚡ DAILY CHECK-IN REWARD CLAIMED!',
      xp: xpBonus,
    });
    setTimeout(() => setRewardToast(null), 4000);
  };

  // Check if all active daily challenges are finished
  const allDailyCompleted = dailyChallenges.length > 0 && dailyChallenges.every((c) => c.claimed || c.completed);

  // Default initial daily goals merged with user goals
  const defaultDailyGoals: Goal[] = [
    {
      id: 'daily_workout_1',
      title: 'Log a Daily Workout',
      description: 'Log a 30-min training, shooting, or conditioning session.',
      target: 1,
      current: 0,
      unit: 'workout',
      rewardXp: 100,
      type: 'daily',
      completed: false,
    },
    {
      id: 'daily_checkin_1',
      title: 'Court & Park Check-In',
      description: 'Check in or discover a local court or field on the map.',
      target: 1,
      current: 0,
      unit: 'check-in',
      rewardXp: 100,
      type: 'daily',
      completed: false,
    },
    {
      id: 'daily_social_1',
      title: 'Post Athlete Update',
      description: 'Share a status update or 30s highlight reel to the social feed.',
      target: 1,
      current: 0,
      unit: 'post',
      rewardXp: 75,
      type: 'daily',
      completed: false,
    },
  ];

  // Merge user's existing goals with standard daily goals avoiding duplicates
  const initialGoalsList = () => {
    const existingIds = new Set((user.goals || []).map((g) => g.id));
    const merged = [...(user.goals || [])];
    defaultDailyGoals.forEach((dg) => {
      if (!existingIds.has(dg.id)) {
        merged.push(dg);
      }
    });
    return merged;
  };

  const [activeGoals, setActiveGoals] = useState<Goal[]>(initialGoalsList);

  // Default Season Goals List (Automatically tailored to user's primarySport & level using useSportFilter logic)
  const targetScale = useMemo(() => {
    const lvl = user.level || 1;
    if (lvl <= 2) return 0.5;
    if (lvl <= 5) return 1.0;
    if (lvl <= 8) return 1.5;
    return 2.0;
  }, [user.level]);

  const defaultSeasonGoalsList = useMemo<Goal[]>(() => {
    const primary = selectedSport || user.primarySport || 'volleyball';
    const lvl = user.level || 1;

    if (primary === 'volleyball') {
      const vb = user.stats?.volleyball || { kills: 34, aces: 12, blocks: 18, digs: 45, assists: 28 };
      const killsTarget = Math.round(50 * targetScale);
      const acesTarget = Math.round(20 * targetScale);
      const blocksTarget = Math.round(30 * targetScale);
      const digsTarget = Math.round(60 * targetScale);

      return [
        {
          id: `sg_vb_kills_l${lvl}`,
          title: `Record ${killsTarget} Spike Kills (Lvl ${lvl} Target) 🏐`,
          description: `Volleyball offensive milestone tailored to your Level ${lvl} athlete tier.`,
          target: killsTarget,
          current: Math.min(killsTarget, vb.kills || 0),
          unit: 'Kills',
          rewardXp: 500,
          type: 'season',
          completed: (vb.kills || 0) >= killsTarget,
          sport: 'volleyball',
        },
        {
          id: `sg_vb_aces_l${lvl}`,
          title: `Deliver ${acesTarget} Service Aces ⚡`,
          description: `Pinpoint serving accuracy target for competitive volleyball sets.`,
          target: acesTarget,
          current: Math.min(acesTarget, vb.aces || 0),
          unit: 'Aces',
          rewardXp: 400,
          type: 'season',
          completed: (vb.aces || 0) >= acesTarget,
          sport: 'volleyball',
        },
        {
          id: `sg_vb_blocks_l${lvl}`,
          title: `Net Wall: ${blocksTarget} Stuff Blocks 🧱`,
          description: `Frontcourt defensive wall milestone for Level ${lvl}.`,
          target: blocksTarget,
          current: Math.min(blocksTarget, vb.blocks || 0),
          unit: 'Blocks',
          rewardXp: 450,
          type: 'season',
          completed: (vb.blocks || 0) >= blocksTarget,
          sport: 'volleyball',
        },
        {
          id: `sg_vb_digs_l${lvl}`,
          title: `Backcourt Anchor: ${digsTarget} Digs 🛡️`,
          description: `Rally revival and defensive floor coverage target.`,
          target: digsTarget,
          current: Math.min(digsTarget, vb.digs || 0),
          unit: 'Digs',
          rewardXp: 350,
          type: 'season',
          completed: (vb.digs || 0) >= digsTarget,
          sport: 'volleyball',
        },
      ];
    } else if (primary === 'basketball') {
      const bk = user.stats?.basketball || { pts: 142, ast: 38, reb: 52, stl: 19, blk: 14 };
      const ptsTarget = Math.round(150 * targetScale);
      const astTarget = Math.round(40 * targetScale);
      const rebTarget = Math.round(50 * targetScale);

      return [
        {
          id: `sg_bk_pts_l${lvl}`,
          title: `Score ${ptsTarget} Points (Lvl ${lvl} Target) 🏀`,
          description: `Scoring title quest tailored to your Level ${lvl} rank.`,
          target: ptsTarget,
          current: Math.min(ptsTarget, bk.pts || 0),
          unit: 'PTS',
          rewardXp: 500,
          type: 'season',
          completed: (bk.pts || 0) >= ptsTarget,
          sport: 'basketball',
        },
        {
          id: `sg_bk_ast_l${lvl}`,
          title: `Dish ${astTarget} Assists 🎯`,
          description: `Playmaking & floor general milestone for Level ${lvl}.`,
          target: astTarget,
          current: Math.min(astTarget, bk.ast || 0),
          unit: 'Assists',
          rewardXp: 400,
          type: 'season',
          completed: (bk.ast || 0) >= astTarget,
          sport: 'basketball',
        },
        {
          id: `sg_bk_reb_l${lvl}`,
          title: `Grab ${rebTarget} Rebounds 🧺`,
          description: `Board crashing defense & second-chance opportunities.`,
          target: rebTarget,
          current: Math.min(rebTarget, bk.reb || 0),
          unit: 'Rebounds',
          rewardXp: 350,
          type: 'season',
          completed: (bk.reb || 0) >= rebTarget,
          sport: 'basketball',
        },
      ];
    } else if (primary === 'soccer') {
      const sc = user.stats?.soccer || { goals: 12, assists: 8, cleanSheets: 3 };
      const goalsTarget = Math.round(15 * targetScale);
      const assistsTarget = Math.round(10 * targetScale);

      return [
        {
          id: `sg_sc_goals_l${lvl}`,
          title: `Score ${goalsTarget} Goals (Lvl ${lvl} Target) ⚽`,
          description: `Striker golden boot milestone.`,
          target: goalsTarget,
          current: Math.min(goalsTarget, sc.goals || 0),
          unit: 'Goals',
          rewardXp: 500,
          type: 'season',
          completed: (sc.goals || 0) >= goalsTarget,
          sport: 'soccer',
        },
        {
          id: `sg_sc_assists_l${lvl}`,
          title: `Create ${assistsTarget} Assists 🅰️`,
          description: `Midfield playmaking milestone for Level ${lvl}.`,
          target: assistsTarget,
          current: Math.min(assistsTarget, sc.assists || 0),
          unit: 'Assists',
          rewardXp: 400,
          type: 'season',
          completed: (sc.assists || 0) >= assistsTarget,
          sport: 'soccer',
        },
      ];
    }
    const gamesTarget = Math.round(20 * targetScale);
    return [
      {
        id: `sg_games_l${lvl}`,
        title: `Log ${gamesTarget} Matches (Lvl ${lvl} Target) 🏆`,
        description: `Consistency milestone for tracking competitive runs.`,
        target: gamesTarget,
        current: Math.min(gamesTarget, (user.winCount || 0) + (user.lossCount || 0)),
        unit: 'Games',
        rewardXp: 300,
        type: 'season',
        completed: ((user.winCount || 0) + (user.lossCount || 0)) >= gamesTarget,
      },
    ];
  }, [user.primarySport, selectedSport, user.level, user.stats, user.winCount, user.lossCount, targetScale]);

  const [seasonGoals, setSeasonGoals] = useState<Goal[]>(defaultSeasonGoalsList);

  useEffect(() => {
    setSeasonGoals(defaultSeasonGoalsList);
  }, [defaultSeasonGoalsList]);
  const [isAddSeasonGoalModalOpen, setIsAddSeasonGoalModalOpen] = useState(false);
  const [seasonGoalTitle, setSeasonGoalTitle] = useState('');
  const [seasonGoalMetric, setSeasonGoalMetric] = useState('Blocks');
  const [seasonGoalTarget, setSeasonGoalTarget] = useState(50);
  const [seasonGoalSport, setSeasonGoalSport] = useState<SportType>('volleyball');
  const [seasonGoalXp, setSeasonGoalXp] = useState(350);

  // Friend Activity Widget Feed State
  interface FriendActivityItem {
    id: string;
    friendId: string;
    friendName: string;
    friendHandle: string;
    friendAvatar: string;
    friendRole: string;
    friendSport: SportType;
    actionTitle: string;
    actionDetail: string;
    timeAgo: string;
    cheersCount: number;
    hasCheered?: boolean;
    level: number;
    winCount: number;
  }

  const [friendActivities, setFriendActivities] = useState<FriendActivityItem[]>([
    {
      id: 'fa_1',
      friendId: 'fr_marcus',
      friendName: 'Marcus Vance',
      friendHandle: '@marcus_vance11',
      friendAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
      friendRole: 'Court General • #11',
      friendSport: 'basketball',
      actionTitle: 'Logged Match Stats 🏀',
      actionDetail: 'Scored 28 PTS, 12 REB & 6 AST in Basketball Pickup Run at Rucker Park',
      timeAgo: '12m ago',
      cheersCount: 14,
      level: 16,
      winCount: 42,
    },
    {
      id: 'fa_2',
      friendId: 'fr_sarah',
      friendName: 'Sarah Lin',
      friendHandle: '@sarah_spikes7',
      friendAvatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&auto=format&fit=crop&q=80',
      friendRole: 'Volleyball Captain • #7',
      friendSport: 'volleyball',
      actionTitle: 'Completed Season Goal 🏐',
      actionDetail: 'Reached Season Milestone: "50 Blocks this Season!" (+350 XP Claimed)',
      timeAgo: '45m ago',
      cheersCount: 22,
      level: 18,
      winCount: 56,
    },
    {
      id: 'fa_3',
      friendId: 'fr_tyler',
      friendName: 'Tyler Brooks',
      friendHandle: '@tyler_striker9',
      friendAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80',
      friendRole: 'Playground Starter • #23',
      friendSport: 'soccer',
      actionTitle: 'Earned Athlete Badge 🏆',
      actionDetail: 'Unlocked "Triple-Double Titan" Badge & claimed +250 XP reward!',
      timeAgo: '2h ago',
      cheersCount: 9,
      level: 12,
      winCount: 29,
    },
    {
      id: 'fa_4',
      friendId: 'fr_jordan',
      friendName: 'Jordan Cole',
      friendHandle: '@jordan_ace1',
      friendAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&auto=format&fit=crop&q=80',
      friendRole: 'Playground Pro 👑',
      friendSport: 'baseball',
      actionTitle: 'Level Up Milestone ⚡',
      actionDetail: 'Reached Level 15 Athlete and achieved Level Title "Franchise Icon"',
      timeAgo: '4h ago',
      cheersCount: 31,
      level: 15,
      winCount: 61,
    },
  ]);

  const [selectedFriendProfile, setSelectedFriendProfile] = useState<FriendActivityItem | null>(null);

  // Toggle Cheer for Friend Activity
  const handleCheerFriendActivity = (activityId: string, friendName: string) => {
    triggerHaptic('light');
    setFriendActivities((prev) =>
      prev.map((act) => {
        if (act.id === activityId) {
          const isCheered = !act.hasCheered;
          return {
            ...act,
            hasCheered: isCheered,
            cheersCount: isCheered ? act.cheersCount + 1 : act.cheersCount - 1,
          };
        }
        return act;
      })
    );

    setRewardToast({
      show: true,
      title: `🔥 CHEER SENT TO ${friendName.toUpperCase()}!`,
      xp: 15,
    });
    setTimeout(() => setRewardToast(null), 3000);
  };

  // Daily Point Goal Feature State
  const [dailyPointTarget, setDailyPointTarget] = useState<number>(25);
  const [bonusLoggedPoints, setBonusLoggedPoints] = useState<number>(12);
  const [isEditingDailyPointGoal, setIsEditingDailyPointGoal] = useState<boolean>(false);
  const [customGoalInput, setCustomGoalInput] = useState<string>('25');
  const [dailyGoalClaimed, setDailyGoalClaimed] = useState<boolean>(false);

  const totalLoggedPointsToday = useMemo(() => {
    let pts = bonusLoggedPoints;
    if (user.statsHistory && Array.isArray(user.statsHistory)) {
      const todayStr = new Date().toISOString().split('T')[0];
      user.statsHistory.forEach((sh: any) => {
        if (sh.date && sh.date.includes(todayStr)) {
          pts += Number(sh.pts || sh.points || sh.kills || 0);
        }
      });
    }
    return pts;
  }, [user.statsHistory, bonusLoggedPoints]);

  const handleQuickLogPoints = (pointsToAdd: number) => {
    triggerHaptic('medium');
    const newTotal = totalLoggedPointsToday + pointsToAdd;
    setBonusLoggedPoints((prev) => prev + pointsToAdd);

    if (newTotal >= dailyPointTarget && !dailyGoalClaimed) {
      triggerHaptic('success');
      if (onEarnXp) {
        onEarnXp(150, `Daily Point Target Achieved (${dailyPointTarget} PTS)!`);
      }
      setRewardToast({
        show: true,
        title: `🔥 DAILY POINT TARGET ACHIEVED (${dailyPointTarget} PTS)!`,
        xp: 150,
      });
      setTimeout(() => setRewardToast(null), 4000);
      setDailyGoalClaimed(true);
    } else {
      setRewardToast({
        show: true,
        title: `⚡ +${pointsToAdd} PTS LOGGED TO DAILY TARGET!`,
        xp: pointsToAdd * 2,
      });
      setTimeout(() => setRewardToast(null), 2500);
    }
  };

  const handleUpdateDailyPointGoal = (newTarget: number) => {
    triggerHaptic('light');
    setDailyPointTarget(newTarget);
    setIsEditingDailyPointGoal(false);
    if (totalLoggedPointsToday < newTarget) {
      setDailyGoalClaimed(false);
    }
  };

  // Add Progress to Season Goal
  const handleProgressSeasonGoal = (goalId: string, increment: number = 5) => {
    triggerHaptic('medium');
    setSeasonGoals((prev) =>
      prev.map((g) => {
        if (g.id === goalId) {
          const nextVal = Math.min(g.target, g.current + increment);
          const isDone = nextVal >= g.target;
          return {
            ...g,
            current: nextVal,
            completed: isDone,
          };
        }
        return g;
      })
    );
  };

  // Claim Season Goal Reward
  const handleClaimSeasonGoalReward = (goalId: string) => {
    triggerHaptic('success');
    setSeasonGoals((prev) =>
      prev.map((g) => {
        if (g.id === goalId) {
          if (onEarnXp) {
            onEarnXp(g.rewardXp, `Season Goal Completed: ${g.title}`);
          }
          setRewardToast({
            show: true,
            title: `🏆 SEASON GOAL ACHIEVED: ${g.title}`,
            xp: g.rewardXp,
          });
          setTimeout(() => setRewardToast(null), 4000);
          return {
            ...g,
            completed: true,
          };
        }
        return g;
      })
    );
  };

  // Create Custom Season Goal
  const handleCreateSeasonGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!seasonGoalTitle.trim()) return;

    const newSeasonGoal: Goal = {
      id: `sg_custom_${Date.now()}`,
      title: seasonGoalTitle.trim(),
      description: `Personal season milestone target in ${getSportLabel(seasonGoalSport)}.`,
      target: seasonGoalTarget,
      current: 0,
      unit: seasonGoalMetric,
      rewardXp: seasonGoalXp,
      type: 'season',
      completed: false,
      sport: seasonGoalSport,
    };

    setSeasonGoals([newSeasonGoal, ...seasonGoals]);
    setIsAddSeasonGoalModalOpen(false);
    setSeasonGoalTitle('');

    setRewardToast({
      show: true,
      title: '🎯 NEW SEASON GOAL SET!',
      xp: 50,
    });
    setTimeout(() => setRewardToast(null), 3500);
  };

  // Helper to determine default workout category based on primary sport
  const getDefaultWorkoutCategory = (s?: SportType) => {
    switch (s) {
      case 'volleyball':
        return 'Volleyball Spiking & Setting Drills 🏐';
      case 'soccer':
        return 'Soccer Dribbling & Shooting Drills ⚽';
      case 'baseball':
        return 'Batting & Pitching Drills ⚾';
      case 'softball':
        return 'Softball Fielding & Pitching 🥎';
      case 'pickleball':
        return 'Pickleball Dinks & Kitchen Drills 🏓';
      case 'football':
        return 'Passing & Route Running Drills 🏈';
      case 'tennis':
        return 'Tennis Serve & Footwork Drills 🎾';
      default:
        return 'Shooting & Free Throws 🏀';
    }
  };

  // Modal States
  const [isWorkoutModalOpen, setIsWorkoutModalOpen] = useState(false);
  const [workoutType, setWorkoutType] = useState(() => getDefaultWorkoutCategory(user.primarySport));
  const [workoutMinutes, setWorkoutMinutes] = useState(30);
  const [workoutNotes, setWorkoutNotes] = useState('');

  // Keep default workout type synced when primarySport changes
  useEffect(() => {
    setWorkoutType(getDefaultWorkoutCategory(user.primarySport));
  }, [user.primarySport]);

  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);
  const [customGoalTitle, setCustomGoalTitle] = useState('');
  const [customGoalDesc, setCustomGoalDesc] = useState('');
  const [customGoalTarget, setCustomGoalTarget] = useState(1);
  const [customGoalXp, setCustomGoalXp] = useState(100);

  // PRO-Tier 30-Day Sparkline Metric State & Data
  const [proMetric, setProMetric] = useState<'pts' | 'winRate' | 'efficiency' | 'workoutMins'>('pts');

  const sparklineData = useMemo(() => {
    const data = [];
    const basePts = user.primarySport === 'basketball' ? 18 : user.primarySport === 'soccer' ? 2 : 12;
    const baseWin = 68;
    const now = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayLabel = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const fullDateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

      const trendFactor = (30 - i) * 0.28;
      const pts = Math.max(6, Math.round(basePts + trendFactor + (Math.sin(i * 0.7) * 4) + ((i % 5) * 0.6)));
      const winRate = Math.min(96, Math.max(40, Math.round(baseWin + (trendFactor * 0.4) + (Math.cos(i * 0.5) * 7))));
      const efficiency = Math.min(38, Math.max(10, Math.round(16 + trendFactor * 0.35 + (Math.sin(i * 1.1) * 3))));
      const workoutMins = Math.round(20 + (i % 4 === 0 ? 35 : 15) + (Math.cos(i) * 8));

      data.push({
        day: dayLabel,
        dateStr: fullDateStr,
        pts,
        winRate,
        efficiency,
        workoutMins,
      });
    }
    return data;
  }, [user.id, user.primarySport]);

  const proMetricConfigs = {
    pts: {
      label: 'Scoring Average (PTS)',
      shortLabel: 'PTS/Game',
      unit: 'PTS',
      color: '#a3e635',
      fillId: 'ptsGradient',
      latest: sparklineData[sparklineData.length - 1]?.pts || 0,
      first: sparklineData[0]?.pts || 1,
      peak: Math.max(...sparklineData.map((d) => d.pts)),
      avg: (sparklineData.reduce((acc, d) => acc + d.pts, 0) / sparklineData.length).toFixed(1),
    },
    winRate: {
      label: 'Win Rate Percentage (%)',
      shortLabel: 'Win Rate %',
      unit: '%',
      color: '#fbbf24',
      fillId: 'winGradient',
      latest: sparklineData[sparklineData.length - 1]?.winRate || 0,
      first: sparklineData[0]?.winRate || 1,
      peak: Math.max(...sparklineData.map((d) => d.winRate)),
      avg: (sparklineData.reduce((acc, d) => acc + d.winRate, 0) / sparklineData.length).toFixed(1),
    },
    efficiency: {
      label: 'PER Efficiency Rating',
      shortLabel: 'PER Rating',
      unit: 'PER',
      color: '#22d3ee',
      fillId: 'effGradient',
      latest: sparklineData[sparklineData.length - 1]?.efficiency || 0,
      first: sparklineData[0]?.efficiency || 1,
      peak: Math.max(...sparklineData.map((d) => d.efficiency)),
      avg: (sparklineData.reduce((acc, d) => acc + d.efficiency, 0) / sparklineData.length).toFixed(1),
    },
    workoutMins: {
      label: 'Daily Workout Minutes',
      shortLabel: 'Workout Vol',
      unit: 'min',
      color: '#f43f5e',
      fillId: 'workGradient',
      latest: sparklineData[sparklineData.length - 1]?.workoutMins || 0,
      first: sparklineData[0]?.workoutMins || 1,
      peak: Math.max(...sparklineData.map((d) => d.workoutMins)),
      avg: (sparklineData.reduce((acc, d) => acc + d.workoutMins, 0) / sparklineData.length).toFixed(0),
    },
  };

  const currentMetricCfg = proMetricConfigs[proMetric];
  const metricPctChange = (((currentMetricCfg.latest - currentMetricCfg.first) / currentMetricCfg.first) * 100).toFixed(1);
  const isPosChange = Number(metricPctChange) >= 0;

  const currentStatSummary = getSportStatSummary(user, currentSport);

  // Use useSportFilter hook for recommended games filtering
  const { filteredPickupGames } = useSportFilter({
    userSport: currentSport,
    athletes: [],
    pickupGames,
    tournaments: [],
  });

  const userCity = user.registeredCity || 'New York';

  // Recommended Games matching user's primarySport & registeredCity
  const recommendedGames = useMemo(() => {
    const cityLower = userCity.toLowerCase();
    const cityMatches = filteredPickupGames.filter((g) => {
      const gCity = ((g as any).city || '').toLowerCase();
      const gAddr = (g.address || '').toLowerCase();
      const gCourt = (g.courtName || '').toLowerCase();
      return gCity.includes(cityLower) || gAddr.includes(cityLower) || gCourt.includes(cityLower);
    });

    if (cityMatches.length > 0) {
      return cityMatches;
    }
    return filteredPickupGames.length > 0 ? filteredPickupGames : pickupGames;
  }, [filteredPickupGames, pickupGames, userCity]);

  // Handle Goal Completion or XP Claim
  const handleCompleteGoal = (goalId: string) => {
    setActiveGoals((prev) =>
      prev.map((g) => {
        if (g.id === goalId) {
          if (!g.completed) {
            if (onEarnXp) {
              onEarnXp(g.rewardXp, g.title);
            } else {
              onClaimGoalReward(goalId);
            }
          }
          return {
            ...g,
            current: g.target,
            completed: true,
          };
        }
        return g;
      })
    );
  };

  // Handle Submitting Log Workout
  const handleSaveWorkout = (e: React.FormEvent) => {
    e.preventDefault();
    // Complete daily workout goal if present
    handleCompleteGoal('daily_workout_1');

    // Also auto-advance any daily challenge related to workout
    setDailyChallenges((prev) =>
      prev.map((ch) => {
        if (ch.id === 'ch_workout_45m') {
          return {
            ...ch,
            current: ch.target,
            completed: true,
          };
        }
        return ch;
      })
    );

    if (!activeGoals.some((g) => g.id === 'daily_workout_1' && g.completed)) {
      if (onEarnXp) {
        onEarnXp(100, `Workout Session (${workoutType} - ${workoutMinutes} mins)`);
      }
    }
    setIsWorkoutModalOpen(false);
    setWorkoutNotes('');
  };

  // Handle Submitting Custom Goal
  const handleCreateCustomGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customGoalTitle.trim()) return;

    const newGoal: Goal = {
      id: `custom_goal_${Date.now()}`,
      title: customGoalTitle.trim(),
      description: customGoalDesc.trim() || 'Daily athlete engagement target',
      target: customGoalTarget,
      current: 0,
      unit: 'target',
      rewardXp: customGoalXp,
      type: 'daily',
      completed: false,
    };

    setActiveGoals([newGoal, ...activeGoals]);
    setIsAddGoalModalOpen(false);
    setCustomGoalTitle('');
    setCustomGoalDesc('');
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white relative">
      {/* Floating Reward Toast Notification */}
      {rewardToast && rewardToast.show && (
        <div className="fixed top-20 right-4 z-50 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-300 text-black p-4 rounded-2xl shadow-2xl border-2 border-white flex items-center space-x-3 animate-bounce">
          <div className="p-2 bg-black text-lime-400 rounded-xl">
            <Sparkles className="w-6 h-6 animate-spin" />
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider block opacity-80">
              Reward Unlocked!
            </span>
            <h4 className="text-sm font-black italic uppercase">
              {rewardToast.title}
            </h4>
            <span className="text-xs font-black font-mono">
              +{rewardToast.xp} BONUS XP ADDED TO PROFILE
            </span>
          </div>
        </div>
      )}

      {/* Top Banner - Hero Greeting & XP Level Card */}
      <div className="relative overflow-hidden rounded-[2.5rem] bg-indigo-900 p-6 md:p-8 shadow-2xl border border-white/10">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-rose-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-10 w-48 h-48 bg-lime-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center space-x-4 md:space-x-6">
            <div className="relative">
              <img
                src={user?.avatar || ''}
                alt={user?.name || 'Athlete'}
                className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover ring-4 ring-lime-400 shadow-xl"
                referrerPolicy="no-referrer"
              />
              <span className="absolute -bottom-2 -right-2 bg-lime-400 text-black font-black italic text-xs px-2.5 py-0.5 rounded-lg shadow">
                #{user?.jerseyNumber || '7'}
              </span>
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-400 text-[11px] font-black italic border border-lime-400/30 uppercase">
                  {user?.role === 'high_school' ? 'High School Varsity' : 'College / League'}
                </span>
                <span className="text-xs text-indigo-200/70 font-bold">• {user?.schoolOrLeague || 'Playground League'}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-black italic tracking-tight mt-1 text-white uppercase flex items-center gap-2 flex-wrap">
                <span>Welcome back, {user?.name || 'Athlete'}!</span>
                {user?.isPro && (
                  <span id="dashboard-pro-badge" className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 text-black text-xs font-black italic uppercase rounded-full shadow-lg shadow-amber-400/30 border border-amber-200">
                    <Crown className="w-3.5 h-3.5 fill-black" />
                    <span>PRO 👑</span>
                  </span>
                )}
              </h1>
              <p className="text-sm text-indigo-200 mt-0.5 flex items-center space-x-2 font-bold">
                <span>{user.position}</span>
                <span>•</span>
                <span className="text-lime-400 font-black italic">{user.levelTitle}</span>
              </p>
            </div>
          </div>

          {/* XP & Level Progress Box */}
          <div className="bg-indigo-950/80 backdrop-blur-md border border-white/10 rounded-[1.8rem] p-5 flex flex-col min-w-[300px] shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-lime-400" />
                <span className="text-sm font-black italic uppercase text-white">Level {user.level} Athlete</span>
              </div>
              <span className="text-xs font-black italic text-lime-400 font-mono">
                {user.xp} / {user.xpToNextLevel} XP
              </span>
            </div>

            {/* Daily Streak Badge near Level Progress Bar */}
            <div className="flex items-center justify-between bg-gradient-to-r from-orange-500/20 via-amber-500/15 to-orange-500/20 px-3 py-1.5 rounded-xl border border-orange-400/40 mb-2.5 shadow-sm">
              <div className="flex items-center space-x-1.5">
                <Flame className="w-4 h-4 text-orange-400 fill-orange-400 animate-pulse" />
                <span className="text-xs font-black italic uppercase text-orange-300 tracking-wider">
                  {user.dailyStreak ?? 1} Day Streak!
                </span>
              </div>
              <span className="text-[10px] text-amber-300/90 font-bold uppercase tracking-tight">
                ⚡ Log stats daily
              </span>
            </div>

            {/* XP Bar */}
            <div className="w-full bg-indigo-900 rounded-full h-3 overflow-hidden p-0.5 border border-white/10">
              <div
                className="bg-lime-400 h-full rounded-full transition-all duration-700 shadow-sm shadow-lime-400"
                style={{ width: `${Math.min(100, Math.round(((user.xp || 0) / (user.xpToNextLevel || 1000)) * 100))}%` }}
              />
            </div>

            <div className="flex items-center justify-between mt-3 text-xs text-indigo-200">
              <span>Record: <strong className="text-lime-400 font-black">{user.winCount}W</strong> - <strong className="text-rose-400 font-black">{user.lossCount}L</strong></span>
              <button
                onClick={() => onNavigateToTab('analytics')}
                className="text-lime-400 font-black italic uppercase hover:underline flex items-center text-[11px]"
              >
                Analytics <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Action CTA Bar inside Hero */}
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                triggerHaptic('medium');
                onOpenStatLog();
              }}
              className="px-6 py-3 rounded-2xl bg-lime-400 hover:bg-lime-300 text-black font-black italic text-sm tracking-tight shadow-lg shadow-lime-400/20 flex items-center space-x-2 transition active:scale-95 uppercase"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span>Log Recent Game</span>
            </button>
            <button
              onClick={() => {
                triggerHaptic('medium');
                onOpenHostGame();
              }}
              className="px-5 py-3 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black italic text-sm border border-rose-400/30 shadow-lg shadow-rose-500/20 flex items-center space-x-2 transition uppercase"
            >
              <Flame className="w-4 h-4 fill-white/20" />
              <span>Host Pickup Game</span>
            </button>
            {onOpenMiniGames && user?.enableMiniGames === true && (
              <button
                onClick={() => {
                  triggerHaptic('medium');
                  onOpenMiniGames();
                }}
                className="px-5 py-3 rounded-2xl bg-indigo-950 hover:bg-indigo-800 text-lime-400 font-black italic text-sm border border-lime-400/40 shadow-lg flex items-center space-x-2 transition uppercase"
              >
                <Trophy className="w-4 h-4 text-lime-400" />
                <span>Mini-Games 🎮</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-4 text-xs font-bold text-indigo-200">
            <div className="flex items-center space-x-1.5 bg-indigo-950/80 px-3 py-1.5 rounded-xl border border-white/10">
              <Zap className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>Streak: <strong className="text-lime-400 font-black italic">{user.dailyStreak ?? 1} DAYS 🔥</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Check-In Reward System Banner */}
      <div className={`p-5 rounded-[2.5rem] border transition-all duration-300 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-5 relative overflow-hidden ${isCheckedInToday
          ? 'bg-emerald-950/70 border-emerald-400/40'
          : 'bg-gradient-to-r from-amber-500/25 via-indigo-950 to-orange-500/25 border-amber-400/60 ring-2 ring-amber-400/20 shadow-amber-400/10'
        }`}>
        <div className="flex items-center space-x-4 z-10">
          <div className={`p-3.5 rounded-2xl shrink-0 ${isCheckedInToday
              ? 'bg-emerald-400 text-black shadow-lg shadow-emerald-400/30'
              : 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-orange-400 text-black shadow-xl shadow-amber-400/30 animate-pulse'
            }`}>
            {isCheckedInToday ? (
              <CheckCircle2 className="w-7 h-7 stroke-[2.5]" />
            ) : (
              <Gift className="w-7 h-7 stroke-[2.5]" />
            )}
          </div>

          <div>
            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
              <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-400 text-black font-mono">
                DAILY APP CHECK-IN REWARD ⚡
              </span>
              <span className="text-xs text-orange-300 font-extrabold italic flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 text-orange-400 fill-orange-400" />
                <span>{user.dailyStreak ?? 1} Day Streak Active</span>
              </span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white tracking-tight mt-1">
              {isCheckedInToday ? 'Daily Reward Claimed for Today!' : 'Claim Daily App Check-In Reward (+100 XP)'}
            </h3>
            <p className="text-xs text-indigo-200/80 font-medium">
              {isCheckedInToday
                ? 'Great job! You checked in today. Open Playground League again tomorrow to keep your daily streak going and earn another +100 XP.'
                : 'Log in daily to maintain your active streak, elevate your athlete level, and claim your free +100 XP bonus!'}
            </p>
          </div>
        </div>

        <button
          disabled={isCheckedInToday}
          onClick={handleDailyCheckIn}
          className={`px-6 py-3.5 rounded-2xl font-black italic uppercase text-xs tracking-wider transition-all transform shrink-0 flex items-center space-x-2 z-10 shadow-xl ${isCheckedInToday
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 cursor-default'
              : 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-black hover:scale-105 active:scale-95 shadow-amber-400/40 animate-bounce'
            }`}
        >
          {isCheckedInToday ? (
            <>
              <Check className="w-4 h-4 stroke-[3]" />
              <span>Checked In Today ✓</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 fill-black" />
              <span>Claim +100 XP Bonus</span>
            </>
          )}
        </button>
      </div>

      {/* Refer & Earn Friends Banner */}
      {onOpenReferralModal && (
        <div className="p-4 sm:p-5 rounded-[2rem] bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-900 border border-amber-400/40 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3.5">
            <div className="p-3 bg-gradient-to-tr from-amber-400 to-yellow-300 text-black rounded-2xl shadow-lg">
              <Gift className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-amber-400/20 text-amber-300 rounded font-mono border border-amber-400/30">
                  REFERRAL BONUS
                </span>
                <span className="text-[10px] text-lime-400 font-mono font-black">+150 XP EACH</span>
              </div>
              <h3 className="text-base sm:text-lg font-black italic uppercase text-white mt-0.5">
                Invite Athletes to Playground League
              </h3>
              <p className="text-xs text-indigo-200/80 font-medium">
                Share your personal link or code via text/SMS. Earn +150 XP whenever a teammate registers!
              </p>
            </div>
          </div>

          <button
            onClick={onOpenReferralModal}
            className="px-5 py-3 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-2xl transition transform hover:scale-105 active:scale-95 shadow-lg shadow-amber-400/20 shrink-0 flex items-center space-x-2"
          >
            <Share2 className="w-4 h-4 stroke-[2.5]" />
            <span>Refer Friends Now</span>
          </button>
        </div>
      )}

      {/* REGIONAL PERFORMANCE & LOCAL HAPPENINGS SECTION */}
      <div className="space-y-6">
        {/* 1. Regional Performance Mini-Card */}
        <RegionalPerformanceCard
          user={user}
          primarySport={currentSport}
          registeredCity={user.registeredCity || user.location?.city}
          registeredState={user.registeredState || user.location?.state}
          onNavigateToLeaderboard={() => onNavigateToTab('leaderboard')}
          onOpenProCheckout={onOpenProCheckout}
        />

        {/* 2. Trending Local Tournaments Carousel */}
        <TrendingLocalTournamentsCarousel
          user={user}
          tournaments={tournaments}
          selectedSport={currentSport}
          registeredCity={user.registeredCity || user.location?.city}
          registeredState={user.registeredState || user.location?.state}
          onNavigateToTab={(t) => onNavigateToTab(t)}
        />

        {/* 3. Local Activity Feed */}
        <LocalActivityFeed
          registeredCity={user.registeredCity || user.location?.city}
          registeredState={user.registeredState || user.location?.state}
          pickupGames={pickupGames}
          selectedSport={currentSport}
          onJoinGame={onJoinGame}
          onNavigateToTab={(t) => onNavigateToTab(t)}
        />
      </div>

      <div className="space-y-4">
        {/* Performance Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-indigo-900/60 p-4 sm:p-5 rounded-[2.5rem] border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-lime-400" />
              <h2 className="text-xl font-black italic uppercase tracking-wide text-white">
                {getSportLabel(currentSport)} Performance
              </h2>
              {currentSport === user.primarySport && (
                <span className="px-2 py-0.5 rounded-md bg-lime-400/20 text-lime-300 border border-lime-400/30 font-black italic text-[10px] uppercase">
                  Primary Sport
                </span>
              )}
            </div>

            {/* Active Sports Selector Pills */}
            <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 sm:pb-0">
              {activeSports.map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSport(s)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap ${currentSport === s
                      ? 'bg-lime-400 text-black shadow-md'
                      : 'bg-indigo-950/80 text-indigo-200 hover:text-white border border-white/10'
                    }`}
                >
                  {getSportLabel(s)}
                </button>
              ))}
              <button
                onClick={onOpenStatLog}
                className="px-2.5 py-1.5 rounded-xl text-xs font-bold text-lime-400 hover:bg-lime-400/10 border border-dashed border-lime-400/30 whitespace-nowrap flex items-center space-x-1"
                title="Log stats for a new sport to add it here"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Log Sport</span>
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Metric Cards for Current Sport */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {currentStatSummary.map((stat, i) => (
            <div
              key={i}
              className="bg-indigo-900/50 p-5 rounded-[2rem] border border-white/10 shadow-lg hover:border-lime-400/50 transition group"
            >
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300/70">
                {stat.label}
              </span>
              <div className={`text-3xl font-black italic mt-1 ${stat.highlight ? 'text-lime-400' : 'text-white'}`}>
                {stat.value}
              </div>
              <span className="text-xs text-indigo-200/80 font-medium mt-0.5 block">
                {stat.sub}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* DAILY POINT GOAL TRACKER CARD */}
      <div className="bg-gradient-to-br from-indigo-900/90 via-indigo-950 to-indigo-900/90 p-6 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400/10 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20 font-black">
              <Target className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-black italic uppercase text-white tracking-tight">
                  Daily Point Target Goal
                </h3>
                <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[9px] font-mono font-black uppercase rounded-full">
                  DAILY GOAL
                </span>
              </div>
              <p className="text-xs text-indigo-200/70 font-semibold">
                Track logged game stats against your personal daily scoring target.
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsEditingDailyPointGoal(!isEditingDailyPointGoal)}
            className="px-3.5 py-1.5 bg-indigo-950 hover:bg-indigo-800 text-lime-300 border border-lime-400/30 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-1 shrink-0 self-start sm:self-auto"
          >
            <span>{isEditingDailyPointGoal ? 'Close Edit' : 'Set Goal Target 🎯'}</span>
          </button>
        </div>

        {/* Quick Target Preset Selector when Editing */}
        {isEditingDailyPointGoal && (
          <div className="p-4 bg-indigo-950/90 rounded-2xl border border-white/10 space-y-3 animate-fadeIn">
            <span className="text-xs font-mono font-bold uppercase text-indigo-200 block">
              Choose Your Daily Point Goal Target:
            </span>
            <div className="flex flex-wrap gap-2">
              {[15, 25, 40, 60, 100].map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleUpdateDailyPointGoal(preset)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${dailyPointTarget === preset
                      ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                      : 'bg-indigo-900 text-indigo-200 hover:text-white hover:bg-indigo-800'
                    }`}
                >
                  {preset} PTS
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-2 pt-1">
              <input
                type="number"
                value={customGoalInput}
                onChange={(e) => setCustomGoalInput(e.target.value)}
                placeholder="Custom target..."
                className="w-32 bg-indigo-900 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white font-bold outline-none focus:ring-2 focus:ring-lime-400"
              />
              <button
                onClick={() => {
                  const val = parseInt(customGoalInput, 10);
                  if (val > 0) handleUpdateDailyPointGoal(val);
                }}
                className="px-3 py-1.5 bg-lime-400 text-black rounded-xl text-xs font-black italic uppercase hover:bg-lime-300 transition"
              >
                Save Target
              </button>
            </div>
          </div>
        )}

        {/* Visual Progress Bar Component */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-mono font-bold">
            <span className="text-indigo-200 flex items-center space-x-1.5">
              <span>TODAY'S LOGGED PROGRESS</span>
              {totalLoggedPointsToday >= dailyPointTarget && (
                <span className="text-lime-400 font-black animate-pulse">🔥 TARGET ACHIEVED!</span>
              )}
            </span>
            <span className="text-lime-400 text-sm font-black">
              {totalLoggedPointsToday} / {dailyPointTarget} PTS ({Math.min(100, Math.round((totalLoggedPointsToday / dailyPointTarget) * 100))}%)
            </span>
          </div>

          {/* Large Animated Progress Bar */}
          <div className="w-full bg-indigo-950 h-5 rounded-2xl overflow-hidden p-1 border border-white/10 shadow-inner relative">
            <div
              className={`h-full rounded-xl transition-all duration-700 ${totalLoggedPointsToday >= dailyPointTarget
                  ? 'bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-300 shadow-lg shadow-lime-400/50'
                  : 'bg-gradient-to-r from-lime-400 to-emerald-400'
                }`}
              style={{ width: `${Math.min(100, Math.round((totalLoggedPointsToday / dailyPointTarget) * 100))}%` }}
            />
          </div>

          <p className="text-[11px] text-indigo-300/80 font-medium">
            {totalLoggedPointsToday >= dailyPointTarget
              ? '🎉 Congratulations! You exceeded your daily point goal today (+150 XP bonus earned).'
              : `🎯 ${dailyPointTarget - totalLoggedPointsToday} PTS remaining to achieve today's daily goal.`}
          </p>
        </div>

        {/* Quick Action Logging Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/10">
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-mono font-bold uppercase text-indigo-300">Quick Log PTS:</span>
            <button
              onClick={() => handleQuickLogPoints(2)}
              className="px-2.5 py-1 bg-indigo-950 hover:bg-indigo-800 text-lime-400 border border-lime-400/30 rounded-lg text-xs font-mono font-bold transition hover:scale-105 active:scale-95"
            >
              +2 PTS
            </button>
            <button
              onClick={() => handleQuickLogPoints(5)}
              className="px-2.5 py-1 bg-indigo-950 hover:bg-indigo-800 text-lime-400 border border-lime-400/30 rounded-lg text-xs font-mono font-bold transition hover:scale-105 active:scale-95"
            >
              +5 PTS
            </button>
            <button
              onClick={() => handleQuickLogPoints(10)}
              className="px-2.5 py-1 bg-indigo-950 hover:bg-indigo-800 text-lime-400 border border-lime-400/30 rounded-lg text-xs font-mono font-bold transition hover:scale-105 active:scale-95"
            >
              +10 PTS
            </button>
          </div>

          <button
            onClick={onOpenStatLog}
            className="px-4 py-2 bg-gradient-to-r from-lime-400 to-emerald-400 text-black rounded-xl font-black italic uppercase text-xs shadow-lg hover:scale-105 transition flex items-center space-x-1.5 border border-lime-300/40"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Log Full Game Stats</span>
          </button>
        </div>
      </div>

      {/* WEEKLY CHALLENGES SECTION */}
      <WeeklyChallenges
        user={user}
        onEarnXp={onEarnXp}
        onOpenStatLog={onOpenStatLog}
      />

      {/* Grid Row 2: Gamification Goals & Upcoming Pickup Games */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Randomized Daily Challenges Column (1 col) */}
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-lime-400 text-black rounded-xl shadow-md shadow-lime-400/20">
                <Target className="w-5 h-5 stroke-[2.5]" />
              </div>
              <div>
                <h3 className="font-black italic uppercase text-white text-base flex items-center">
                  <span>Daily Quests</span>
                  <span className="ml-2 px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[9px] font-mono font-bold rounded-full">
                    Randomized
                  </span>
                </h3>
                <span className="text-[10px] text-indigo-300 font-semibold block">
                  Resets in 10h 15m
                </span>
              </div>
            </div>

            {/* Reroll Button */}
            <button
              onClick={handleRerollChallenges}
              disabled={isShuffling}
              className="p-2 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-xl transition flex items-center space-x-1"
              title="Shuffle & Reroll Daily Challenges"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isShuffling ? 'animate-spin text-lime-400' : ''}`} />
              <span className="text-[10px] font-black uppercase hidden sm:inline">Reroll</span>
            </button>
          </div>

          <p className="text-xs text-indigo-200/70 font-medium">
            Randomized goals assigned daily! Progress targets, claim XP bonuses, and unlock the Daily Gold Chest.
          </p>

          {/* Randomized Challenges List */}
          <div className="space-y-3">
            {dailyChallenges.map((ch) => {
              const isFinished = ch.completed || ch.current >= ch.target;
              const percent = Math.min(100, Math.round((ch.current / ch.target) * 100));

              return (
                <div
                  key={ch.id}
                  className={`p-4 rounded-2xl border transition-all duration-300 space-y-2.5 ${ch.claimed
                      ? 'bg-indigo-950/40 border-lime-400/30 opacity-70'
                      : isFinished
                        ? 'bg-gradient-to-r from-indigo-900 to-indigo-950 border-lime-400 ring-1 ring-lime-400/40 shadow-lg'
                        : 'bg-indigo-950/80 border-white/10 hover:border-white/20'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-2.5">
                      <span className="text-xl p-1 bg-indigo-900 rounded-xl border border-white/10">
                        {ch.icon}
                      </span>
                      <div>
                        <h4 className="font-extrabold text-sm text-white flex items-center">
                          <span>{ch.title}</span>
                        </h4>
                        <p className="text-[11px] text-indigo-200/70 mt-0.5 font-medium leading-tight">
                          {ch.description}
                        </p>
                      </div>
                    </div>

                    <span className="shrink-0 text-xs font-black italic text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-lg border border-amber-400/30 font-mono">
                      +{ch.rewardXp} XP
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-indigo-900 h-2.5 rounded-full overflow-hidden p-0.5 border border-white/5">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${ch.claimed || isFinished ? 'bg-lime-400 shadow-sm shadow-lime-400' : 'bg-rose-500'
                          }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono font-bold text-indigo-200">
                      {ch.current}/{ch.target} {ch.unit}
                    </span>
                  </div>

                  {/* Action Shortcuts & Claim Button */}
                  <div className="pt-1 flex items-center justify-between">
                    <div className="flex items-center space-x-1.5">
                      {/* Advance Goal Simulator / Action */}
                      {!isFinished && (
                        <button
                          type="button"
                          onClick={() => handleProgressChallenge(ch.id)}
                          className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-white/10 text-[10px] font-extrabold uppercase rounded-lg transition"
                        >
                          +1 Progress
                        </button>
                      )}

                      {ch.actionTab && !isFinished && (
                        <button
                          type="button"
                          onClick={() => onNavigateToTab(ch.actionTab)}
                          className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-lime-400 text-[10px] font-extrabold uppercase rounded-lg transition"
                        >
                          Go to Tab →
                        </button>
                      )}
                    </div>

                    {ch.claimed ? (
                      <span className="flex items-center space-x-1 text-[11px] font-black italic text-lime-400 bg-lime-400/10 px-2.5 py-1 rounded-xl border border-lime-400/30">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Claimed</span>
                      </span>
                    ) : isFinished ? (
                      <button
                        type="button"
                        onClick={() => handleClaimChallengeReward(ch.id)}
                        className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/30 transition animate-pulse flex items-center space-x-1"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Claim +{ch.rewardXp} XP</span>
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono font-bold text-indigo-300/60 uppercase">
                        In Progress
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Daily Gold Chest Bonus Card */}
          <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 border border-amber-400/40 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Gift className="w-5 h-5 text-amber-400" />
                <span className="text-xs font-black italic uppercase text-amber-300">
                  Daily Gold Chest Bonus
                </span>
              </div>

              <span className="text-[10px] font-mono font-extrabold text-amber-300">
                {dailyChallenges.filter((c) => c.claimed || c.completed).length}/3 Completed
              </span>
            </div>

            {isChestClaimed ? (
              <div className="text-center py-2 text-xs font-black italic text-lime-400 flex items-center justify-center space-x-1">
                <CheckCircle2 className="w-4 h-4" />
                <span>Gold Chest Reward Claimed (+300 XP)</span>
              </div>
            ) : allDailyCompleted ? (
              <button
                onClick={handleClaimDailyChest}
                className="w-full py-2.5 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-black font-black italic text-xs uppercase rounded-xl shadow-xl shadow-amber-500/30 transition animate-bounce flex items-center justify-center space-x-1.5"
              >
                <Coins className="w-4 h-4 fill-black" />
                <span>Open Gold Chest (+300 XP Bonus)</span>
              </button>
            ) : (
              <p className="text-[11px] text-amber-200/70 font-medium italic text-center">
                Complete all 3 daily challenges to unlock +300 Bonus XP & Streak Shield!
              </p>
            )}
          </div>

          {/* Buttons to Log Workout or Add Custom Goal */}
          <div className="pt-2 flex items-center space-x-2">
            <button
              onClick={() => setIsWorkoutModalOpen(true)}
              className="flex-1 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-lime-400 border border-lime-400/30 rounded-2xl font-black italic uppercase text-xs transition flex items-center justify-center space-x-1.5"
            >
              <Dumbbell className="w-4 h-4" />
              <span>Log Workout</span>
            </button>

            <button
              onClick={() => setIsAddGoalModalOpen(true)}
              className="px-3 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-300 border border-white/10 rounded-2xl font-black italic uppercase text-xs transition flex items-center space-x-1"
              title="Add Custom Daily Goal"
            >
              <Plus className="w-4 h-4" />
              <span>Goal</span>
            </button>
          </div>
        </div>

        {/* Recommended Upcoming Pickup Games Column (2 cols) */}
        <div className="lg:col-span-2 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-lime-400" />
              <div>
                <h3 className="font-black italic uppercase text-white text-base">
                  Recommended Pickup Runs
                </h3>
                <p className="text-[11px] text-indigo-300 font-mono">
                  Filtered for <span className="text-lime-300 font-bold capitalize">{currentSport}</span> in <span className="text-cyan-300 font-bold">{userCity}</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => onNavigateToTab('games')}
              className="text-xs font-black italic uppercase text-lime-400 hover:underline shrink-0"
            >
              Browse All Games ({pickupGames.length}) →
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendedGames.slice(0, 4).map((game) => {
              const isJoined = game.playersList.some((p) => p.id === user.id);
              const isSportMatch = game.sport === currentSport;

              return (
                <div
                  key={game.id}
                  className="p-4 rounded-2xl border border-white/10 bg-indigo-950/80 hover:border-lime-400/50 transition flex flex-col justify-between space-y-3 relative overflow-hidden"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1.5">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black italic uppercase bg-lime-400 text-black">
                          {game.sport} • {game.level}
                        </span>
                        {isSportMatch && (
                          <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-[9px] font-black uppercase rounded-full border border-amber-400/30">
                            🎯 Match
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-mono font-bold text-indigo-200 flex items-center">
                        <Users className="w-3.5 h-3.5 mr-1 text-lime-400" />
                        {game.currentPlayers}/{game.maxPlayers}
                      </span>
                    </div>

                    <h4 className="font-extrabold text-sm text-white line-clamp-1">
                      {game.title}
                    </h4>

                    <div className="space-y-1 text-xs text-indigo-200/80 font-medium">
                      <p className="flex items-center">
                        <MapPin className="w-3.5 h-3.5 mr-1 text-rose-400 shrink-0" />
                        <span className="truncate">{game.courtName}</span>
                      </p>
                      <p className="flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1 text-lime-400 shrink-0" />
                        <span>{game.dateTime}</span>
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={game.hostAvatar}
                        alt={game.hostName}
                        className="w-6 h-6 rounded-full object-cover ring-1 ring-lime-400"
                        referrerPolicy="no-referrer"
                      />
                      <span className="text-xs text-indigo-200 font-bold truncate max-w-[100px]">
                        {game.hostName}
                      </span>
                    </div>

                    <button
                      onClick={() => onJoinGame(game.id)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${isJoined
                          ? 'bg-lime-400/20 text-lime-300 border border-lime-400/40'
                          : 'bg-rose-500 hover:bg-rose-600 text-white shadow-md shadow-rose-500/20'
                        }`}
                    >
                      {isJoined ? 'Joined ✓' : 'Join Game'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* GRID ROW 2.5: SEASON GOALS & FRIEND ACTIVITY WIDGET */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SEASON GOALS COMPONENT */}
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-gradient-to-br from-lime-400 to-emerald-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
                <Target className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <h3 className="font-black italic uppercase text-white text-lg flex items-center gap-2">
                  <span>Season Goals</span>
                  <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[10px] font-mono font-bold rounded-full">
                    {seasonGoals.filter((g) => g.completed).length}/{seasonGoals.length} MET
                  </span>
                </h3>
                <p className="text-xs text-indigo-200/70 font-medium">
                  Define personal season milestones. Auto-updates as you log new matches!
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsAddSeasonGoalModalOpen(true)}
              className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1 shrink-0"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span>Define Goal</span>
            </button>
          </div>

          {/* Season Goals List */}
          <div className="space-y-3">
            {seasonGoals.map((goal) => {
              const isFinished = goal.completed || goal.current >= goal.target;
              const percent = Math.min(100, Math.round((goal.current / goal.target) * 100));

              return (
                <div
                  key={goal.id}
                  className={`p-4 rounded-2xl border transition-all duration-300 space-y-3 ${goal.completed
                      ? 'bg-gradient-to-r from-emerald-950/80 to-indigo-950 border-lime-400 shadow-lg'
                      : 'bg-indigo-950/80 border-white/10 hover:border-lime-400/40'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-extrabold text-sm text-white">{goal.title}</span>
                        {goal.sport && (
                          <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-indigo-900 text-indigo-200 border border-white/10 uppercase">
                            {goal.sport}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-indigo-200/70 mt-0.5 font-medium">
                        {goal.description}
                      </p>
                    </div>

                    <span className="text-xs font-black italic text-lime-400 bg-lime-400/10 px-2.5 py-0.5 rounded-lg border border-lime-400/30 font-mono shrink-0 ml-2">
                      +{goal.rewardXp} XP
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-indigo-200 font-bold">
                        Progress: <strong className="text-lime-400 font-black">{goal.current}</strong> / {goal.target} {goal.unit}
                      </span>
                      <span className="text-lime-300 font-black">{percent}%</span>
                    </div>

                    <div className="w-full bg-indigo-900 h-3 rounded-full overflow-hidden p-0.5 border border-white/10">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${isFinished ? 'bg-gradient-to-r from-lime-400 to-emerald-400 shadow-md shadow-lime-400' : 'bg-lime-400'
                          }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>

                  {/* Actions & Auto-update Trigger */}
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={onOpenStatLog}
                        className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-lime-400 text-[10px] font-black uppercase rounded-lg border border-lime-400/30 transition flex items-center space-x-1"
                      >
                        <Plus className="w-3 h-3" />
                        <span>Log Match Stats</span>
                      </button>

                      {!isFinished && (
                        <button
                          type="button"
                          onClick={() => handleProgressSeasonGoal(goal.id, 5)}
                          className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 text-[10px] font-extrabold uppercase rounded-lg border border-white/10 transition"
                        >
                          +5 Progress
                        </button>
                      )}
                    </div>

                    {goal.completed ? (
                      <span className="inline-flex items-center space-x-1 text-[11px] font-black italic text-lime-400 bg-lime-400/20 px-2.5 py-1 rounded-xl border border-lime-400/40">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Completed ✓</span>
                      </span>
                    ) : isFinished ? (
                      <button
                        type="button"
                        onClick={() => handleClaimSeasonGoalReward(goal.id)}
                        className="px-3.5 py-1 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/30 transition animate-bounce flex items-center space-x-1"
                      >
                        <Sparkles className="w-3.5 h-3.5 fill-black" />
                        <span>Claim +{goal.rewardXp} XP</span>
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-indigo-300/60 font-bold uppercase">
                        Auto-updates on Stat Logs
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FRIEND ACTIVITY WIDGET */}
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl shadow-lg shadow-indigo-500/20">
                <Users className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <h3 className="font-black italic uppercase text-white text-lg flex items-center gap-2">
                  <span>Friend Activity</span>
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[10px] font-mono font-bold rounded-full">
                    LIVE FEED
                  </span>
                </h3>
                <p className="text-xs text-indigo-200/70 font-medium">
                  Recent achievements & stat-log updates from your network.
                </p>
              </div>
            </div>

            <button
              onClick={() => onNavigateToTab('community')}
              className="text-xs font-black italic uppercase text-lime-400 hover:underline flex items-center space-x-1 shrink-0"
            >
              <span>Community Hub</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Activity Feed */}
          <div className="space-y-3">
            {friendActivities.map((act) => (
              <div
                key={act.id}
                className="p-3.5 rounded-2xl bg-indigo-950/80 border border-white/10 hover:border-indigo-400/40 transition flex items-start space-x-3 group"
              >
                {/* Clickable Friend Avatar */}
                <button
                  type="button"
                  onClick={() => setSelectedFriendProfile(act)}
                  className="relative shrink-0 group-hover:scale-105 transition transform"
                  title={`View ${act.friendName}'s Public Athlete Profile`}
                >
                  <img
                    src={act.friendAvatar}
                    alt={act.friendName}
                    className="w-11 h-11 rounded-2xl object-cover ring-2 ring-lime-400 shadow-md"
                    referrerPolicy="no-referrer"
                  />
                  <span className="absolute -bottom-1 -right-1 bg-black text-white text-[9px] font-black p-0.5 rounded-full border border-lime-400">
                    Lv.{act.level}
                  </span>
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedFriendProfile(act)}
                      className="text-xs font-black text-white hover:text-lime-400 transition truncate text-left"
                    >
                      {act.friendName} <span className="text-[10px] text-indigo-300 font-bold font-mono">({act.friendHandle})</span>
                    </button>
                    <span className="text-[10px] font-mono text-indigo-300/70 shrink-0 ml-2">
                      {act.timeAgo}
                    </span>
                  </div>

                  <span className="text-[10px] font-black uppercase text-lime-400 tracking-wider block">
                    {act.actionTitle}
                  </span>

                  <p className="text-xs text-indigo-200/90 font-medium leading-snug line-clamp-2">
                    {act.actionDetail}
                  </p>

                  {/* Footer & Cheer Button */}
                  <div className="pt-1.5 flex items-center justify-between border-t border-white/5">
                    <span className="text-[10px] text-indigo-300 font-bold">
                      {act.friendRole}
                    </span>

                    <button
                      type="button"
                      onClick={() => handleCheerFriendActivity(act.id, act.friendName)}
                      className={`px-2.5 py-1 rounded-xl text-[10px] font-black italic uppercase transition flex items-center space-x-1 ${act.hasCheered
                          ? 'bg-rose-500 text-white shadow-md shadow-rose-500/30'
                          : 'bg-indigo-900 text-indigo-200 hover:bg-rose-500/20 hover:text-rose-300 border border-white/10'
                        }`}
                    >
                      <Flame className={`w-3 h-3 ${act.hasCheered ? 'fill-white' : 'text-orange-400 fill-orange-400'}`} />
                      <span>{act.cheersCount} Cheer</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Badges Showcase Grid */}
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Award className="w-5 h-5 text-amber-400" />
            <h3 className="font-black italic uppercase text-white text-base">
              Unlocked Athlete Badges ({user.badges.filter((b) => b.unlocked).length}/{user.badges.length})
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {user.badges.map((badge) => (
            <div
              key={badge.id}
              className={`p-3.5 rounded-2xl border text-center flex flex-col items-center justify-center space-y-1.5 transition ${badge.unlocked
                  ? 'bg-indigo-950 border-lime-400/40 text-white shadow-lg'
                  : 'bg-indigo-950/40 border-white/5 text-indigo-300/40 opacity-50'
                }`}
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-xl ${badge.unlocked ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-black shadow-md' : 'bg-indigo-900 text-indigo-400'
                }`}>
                🏆
              </div>
              <span className="text-xs font-black italic uppercase line-clamp-1">{badge.name}</span>
              <span className="text-[10px] text-indigo-200/60 font-medium line-clamp-2">
                {badge.description}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* PRO MEMBERSHIP BANNER & TOGGLE CARD */}
      <div className={`relative overflow-hidden rounded-[2.5rem] p-6 md:p-7 transition-all duration-500 border-2 shadow-2xl ${user.isPro
          ? 'bg-gradient-to-r from-amber-500/20 via-indigo-900 to-indigo-950 border-amber-400/50 shadow-amber-400/10'
          : 'bg-gradient-to-r from-indigo-900 via-indigo-950 to-slate-900 border-lime-400/30'
        }`}>
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 relative z-10">
          <div className="flex items-start md:items-center space-x-4">
            <div className={`p-3.5 rounded-2xl shrink-0 shadow-lg ${user.isPro ? 'bg-gradient-to-tr from-amber-400 to-yellow-300 text-black' : 'bg-indigo-900 text-amber-400 border border-amber-400/30'
              }`}>
              <Crown className={`w-7 h-7 ${user.isPro ? 'fill-black' : ''}`} />
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full font-mono ${user.isPro ? 'bg-amber-400 text-black' : 'bg-lime-400/20 text-lime-300 border border-lime-400/40'
                  }`}>
                  {user.isPro ? 'PRO MEMBER 👑' : 'RECOMMENDED UPGRADE'}
                </span>
                <span className="text-xs text-indigo-200/70 font-bold">• Playground Pro Tier ($9.99/mo)</span>
              </div>

              <h3 className="text-lg md:text-xl font-black italic uppercase text-white tracking-tight">
                {user.isPro
                  ? '👑 PRO Membership Active — Advanced Analytics & Ad-Free Unlocked'
                  : '⭐ Upgrade to Playground PRO Tier'}
              </h3>

              <p className="text-xs text-indigo-200/80 font-medium">
                {user.isPro
                  ? 'Your account is enjoying deep radar performance analytics, zero ad interruptions, and priority match queues.'
                  : 'Unlock advanced player trend projections, interactive court heatmaps, and a 100% ad-free experience.'}
              </p>

              {/* Perks Pills */}
              <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px]">
                <span className="inline-flex items-center space-x-1 text-amber-300 font-bold bg-amber-400/10 px-2.5 py-1 rounded-lg border border-amber-400/20">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Advanced Analytics Visualization</span>
                </span>
                <span className="inline-flex items-center space-x-1 text-lime-300 font-bold bg-lime-400/10 px-2.5 py-1 rounded-lg border border-lime-400/20">
                  <Zap className="w-3.5 h-3.5" />
                  <span>100% Ad-Free Browsing</span>
                </span>
                <span className="inline-flex items-center space-x-1 text-indigo-200 font-bold bg-indigo-900/60 px-2.5 py-1 rounded-lg border border-white/10">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
                  <span>Gold Crown Badge</span>
                </span>
              </div>
            </div>
          </div>

          {/* Toggle Button & Action */}
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 bg-indigo-950/90 p-3 rounded-2xl border border-white/10">
            <div className="text-left sm:text-right text-xs">
              <span className="text-[10px] uppercase font-black tracking-wider text-indigo-300 block">
                Subscription Switch:
              </span>
              <span className="font-bold text-white">
                {user.isPro ? 'PRO Member Active' : 'Free Member Tier'}
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                if (!user.isPro) {
                  if (onOpenProCheckout) {
                    onOpenProCheckout();
                  } else {
                    onNavigateToTab('profile');
                  }
                } else {
                  onNavigateToTab('profile');
                }
              }}
              className={`px-5 py-2.5 rounded-xl font-black italic uppercase text-xs tracking-wide flex items-center space-x-2 transition shadow-lg ${user.isPro
                  ? 'bg-amber-400 text-black hover:bg-amber-300'
                  : 'bg-lime-400 text-black hover:bg-lime-300 shadow-lime-400/20 scale-105'
                }`}
            >
              <Crown className="w-4 h-4 fill-black" />
              <span>{user.isPro ? 'PRO Active' : 'Upgrade to PRO'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* AD-FREE BROWSING DEMO / SPONSOR BANNER (Hidden if user is PRO) */}
      {!user.isPro ? (
        <div className="bg-slate-900/90 border border-amber-400/30 p-3 rounded-2xl flex items-center justify-between text-xs text-slate-300">
          <div className="flex items-center space-x-2">
            <span className="bg-amber-400/20 text-amber-300 text-[9px] font-black uppercase px-2 py-0.5 rounded font-mono border border-amber-400/40">
              SPONSORED AD
            </span>
            <p className="font-semibold text-slate-200">
              ⚡ <strong className="text-amber-300">Pro Nike Dunk & Court Sneaker Sale:</strong> Get 20% off indoor court shoes at local retailers!
            </p>
          </div>
          <button
            type="button"
            onClick={() => onNavigateToTab('profile')}
            className="text-[10px] font-black uppercase text-amber-400 hover:underline shrink-0 ml-2"
          >
            Remove Ads with PRO 👑
          </button>
        </div>
      ) : (
        <div className="bg-emerald-950/40 border border-emerald-500/30 p-2.5 rounded-2xl flex items-center justify-between text-xs text-emerald-300">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="font-bold">Ad-Free Browsing Active 🛡️ — Sponsored banners and ads removed for PRO Athletes!</span>
          </div>
          <span className="text-[10px] font-mono text-emerald-400/80 font-bold uppercase">PRO Tier Active</span>
        </div>
      )}

      {/* PRO-TIER EXCLUSIVE: 30-DAY PERFORMANCE TREND SPARKLINE WIDGET */}
      <ProRestrictedWrapper
        user={user}
        onOpenProCheckout={(msg) => {
          if (onOpenProCheckout) onOpenProCheckout(msg || 'This 30-day performance trend summary requires a PRO membership to unlock.');
        }}
        featureTitle="30-Day Performance Trend Summary"
        featureDescription="Track interactive sparklines, peak metric projections, growth deltas, and 30-day stat trajectory with Playground PRO."
        customContextMessage="This historical performance deep-dive requires a PRO membership to unlock."
      >
        <div className="relative overflow-hidden rounded-[2.5rem] p-6 border-2 border-amber-400/40 bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 shadow-2xl shadow-amber-400/10">
          {/* Ambient Top Glow */}
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-10 w-60 h-60 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 space-y-5">
            {/* Header Row */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-tr from-amber-400 to-yellow-300 text-black rounded-2xl shadow-lg ring-2 ring-amber-400/30">
                  <Crown className="w-6 h-6 fill-black" />
                </div>

                <div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2.5 py-0.5 bg-amber-400 text-black text-[10px] font-black uppercase font-mono rounded-full tracking-wider">
                      PRO-TIER EXCLUSIVE
                    </span>
                    <span className="text-xs text-indigo-300 font-mono font-bold">• 30-DAY SPARKLINE ANALYTICS</span>
                  </div>
                  <h3 className="text-lg md:text-xl font-black italic uppercase tracking-tight text-white mt-0.5 flex items-center gap-2">
                    <span>30-Day Performance Trend Summary</span>
                    {verifyProAccess(user) && (
                      <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-mono font-bold rounded-md">
                        LIVE DATA
                      </span>
                    )}
                  </h3>
                </div>
              </div>

              {/* Metric Switcher Tabs */}
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 lg:pb-0">
                {(['pts', 'winRate', 'efficiency', 'workoutMins'] as const).map((m) => {
                  const cfg = proMetricConfigs[m];
                  const isSelected = proMetric === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        if (!verifyProAccess(user)) {
                          if (onOpenProCheckout) {
                            onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.');
                          } else {
                            onNavigateToTab('profile');
                          }
                        } else {
                          setProMetric(m);
                        }
                      }}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-black italic uppercase transition whitespace-nowrap flex items-center space-x-1 ${isSelected
                          ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20 ring-2 ring-amber-300'
                          : 'bg-indigo-900/80 text-indigo-200 hover:text-white border border-white/10'
                        }`}
                    >
                      <span>{cfg.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stat Summary Metrics Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-indigo-900/50 p-3.5 rounded-2xl border border-white/10">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300 block">
                  Latest {currentMetricCfg.shortLabel}
                </span>
                <div className="text-2xl font-black italic text-white mt-0.5 flex items-baseline gap-1">
                  <span>{currentMetricCfg.latest}</span>
                  <span className="text-xs font-mono font-normal text-indigo-300">{currentMetricCfg.unit}</span>
                </div>
              </div>

              <div className="bg-indigo-900/50 p-3.5 rounded-2xl border border-white/10">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300 block">
                  30-Day Growth Delta
                </span>
                <div className={`text-2xl font-black italic mt-0.5 flex items-center gap-1 ${isPosChange ? 'text-lime-400' : 'text-rose-400'
                  }`}>
                  {isPosChange ? <ArrowUpRight className="w-5 h-5 stroke-[3]" /> : <ArrowDownRight className="w-5 h-5 stroke-[3]" />}
                  <span>{isPosChange ? `+${metricPctChange}%` : `${metricPctChange}%`}</span>
                </div>
              </div>

              <div className="bg-indigo-900/50 p-3.5 rounded-2xl border border-white/10">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300 block">
                  30-Day Peak High
                </span>
                <div className="text-2xl font-black italic text-amber-300 mt-0.5 flex items-baseline gap-1">
                  <span>{currentMetricCfg.peak}</span>
                  <span className="text-xs font-mono font-normal text-indigo-300">{currentMetricCfg.unit}</span>
                </div>
              </div>

              <div className="bg-indigo-900/50 p-3.5 rounded-2xl border border-white/10">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300 block">
                  30-Day Average
                </span>
                <div className="text-2xl font-black italic text-cyan-300 mt-0.5 flex items-baseline gap-1">
                  <span>{currentMetricCfg.avg}</span>
                  <span className="text-xs font-mono font-normal text-indigo-300">{currentMetricCfg.unit}</span>
                </div>
              </div>
            </div>

            {/* Sparkline Chart Container */}
            <div className="relative pt-2">
              {/* Recharts AreaChart Sparkline */}
              <div className="w-full h-56 transition-all duration-300">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id={currentMetricCfg.fillId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={currentMetricCfg.color} stopOpacity={0.6} />
                        <stop offset="95%" stopColor={currentMetricCfg.color} stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" vertical={false} />
                    <XAxis
                      dataKey="day"
                      stroke="#a5b4fc"
                      fontSize={10}
                      tickLine={false}
                      interval={4}
                    />
                    <YAxis
                      stroke="#a5b4fc"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={<CustomSparklineTooltip unit={currentMetricCfg.unit} />}
                    />
                    <Area
                      type="monotone"
                      dataKey={proMetric}
                      stroke={currentMetricCfg.color}
                      strokeWidth={3}
                      fillOpacity={1}
                      fill={`url(#${currentMetricCfg.fillId})`}
                      activeDot={{ r: 6, fill: '#ffffff', stroke: currentMetricCfg.color, strokeWidth: 3 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </ProRestrictedWrapper>

      {/* ADVANCED CAREER INSIGHTS CARD */}
      <AdvancedInsightsProCard
        user={user}
        onOpenProCheckout={(msg) => {
          if (onOpenProCheckout) onOpenProCheckout(msg || 'This historical performance deep-dive requires a PRO membership to unlock.');
        }}
      />

      {/* SAVED LOCATIONS & FAVORITE COURTS SECTION (AT BOTTOM OF HOME PAGE) */}
      {(() => {
        const effectiveFavIds = favoriteCourtIds.length > 0 ? favoriteCourtIds : (user.favoriteCourtIds || []);
        const savedCourtsList = courts.filter((c) => effectiveFavIds.includes(c.id));
        const pendingSyncCount = pendingQueue.filter((item) => item.status === 'pending' || item.status === 'failed').length;

        const handleDirectionsLink = (court: CourtPOI, e?: React.MouseEvent) => {
          if (e) e.stopPropagation();
          const destination = encodeURIComponent(`${court.name}, ${court.address}`);
          const isAppleDevice = typeof navigator !== 'undefined' && /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
          const url = isAppleDevice
            ? `https://maps.apple.com/?daddr=${court.lat},${court.lng}&q=${destination}`
            : `https://www.google.com/maps/dir/?api=1&destination=${court.lat},${court.lng}&destination_place_id=${destination}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        };

        return (
          <div className="bg-slate-900/80 p-5 sm:p-6 rounded-3xl border border-white/10 shadow-lg space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-rose-500/20 rounded-xl border border-rose-500/30 text-rose-400 shrink-0">
                  <Heart className="w-5 h-5 fill-rose-500 stroke-[2.5]" />
                </div>
                <div>
                  <div className="flex items-center flex-wrap gap-2">
                    <h3 className="font-black italic uppercase text-white text-base tracking-wide">
                      Saved Locations & Favorite Courts
                    </h3>
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-mono font-bold rounded-full">
                      {savedCourtsList.length} SAVED
                    </span>

                    {/* PENDING SYNC VISUAL INDICATOR BADGE */}
                    {pendingSyncCount > 0 && (
                      <span
                        className="px-2.5 py-0.5 bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[10px] font-mono font-bold rounded-full flex items-center gap-1 shadow-sm animate-pulse"
                        title={`${pendingSyncCount} pending offline items awaiting cloud synchronization`}
                      >
                        <RefreshCw className="w-3 h-3 text-amber-400 animate-spin shrink-0" />
                        <span>{pendingSyncCount} PENDING SYNC</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-indigo-200/70 font-medium mt-0.5">
                    Quickly access directions, active player counts, and host pickup games at your bookmarked courts.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onNavigateToTab('map')}
                className="text-xs font-black italic uppercase text-lime-400 hover:underline flex items-center gap-1 shrink-0 self-start sm:self-auto"
              >
                <span>View Court Map</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-indigo-950/80 rounded-2xl border border-lime-400/30">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-lime-400/20 text-lime-400 rounded-xl shrink-0">
                  <Zap className="w-4 h-4 fill-lime-400" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-black uppercase italic text-white">Favorite Court Alerts</span>
                    <span className={`text-[9px] font-mono font-black px-2 py-0.5 rounded uppercase border ${(user.favoriteCourtAlertsEnabled ?? true)
                        ? 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                        : 'bg-indigo-950 text-indigo-300 border-white/10'
                      }`}>
                      {(user.favoriteCourtAlertsEnabled ?? true) ? 'ACTIVE 🔔' : 'OFF'}
                    </span>
                  </div>
                  <p className="text-[11px] text-indigo-200/70 font-medium mt-0.5">
                    Triggers push notifications when a new game is posted at your favorited venues.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (onUpdateProfile) {
                    onUpdateProfile({ favoriteCourtAlertsEnabled: !(user.favoriteCourtAlertsEnabled ?? true) });
                  }
                }}
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none shrink-0 ${(user.favoriteCourtAlertsEnabled ?? true) ? 'bg-lime-400' : 'bg-slate-700'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-black transition-transform ${(user.favoriteCourtAlertsEnabled ?? true) ? 'translate-x-7' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {savedCourtsList.length === 0 ? (
              <div className="p-8 rounded-2xl bg-indigo-950/60 border border-dashed border-white/15 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-indigo-900 flex items-center justify-center text-rose-400 border border-white/10">
                  <Heart className="w-6 h-6 stroke-[2]" />
                </div>
                <div>
                  <h4 className="font-extrabold text-sm text-white">No Favorite Courts Saved Yet</h4>
                  <p className="text-xs text-indigo-200/70 max-w-md mx-auto mt-1 font-medium">
                    Explore sports venues on the Interactive Map and click the heart icon to save your go-to courts for fast navigation and instant check-ins!
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigateToTab('map')}
                  className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-xl shadow-lg shadow-lime-400/20 transition inline-flex items-center space-x-1.5"
                >
                  <MapPin className="w-4 h-4" />
                  <span>Explore Court Map 🗺️</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {savedCourtsList.map((court) => {
                  const sportEmoji =
                    court.sport === 'basketball' ? '🏀' :
                      court.sport === 'baseball' ? '⚾' :
                        court.sport === 'softball' ? '🥎' :
                          court.sport === 'pickleball' ? '🏓' :
                            court.sport === 'soccer' ? '⚽' : '🏐';

                  return (
                    <div
                      key={court.id}
                      className="p-3.5 rounded-2xl bg-indigo-950/90 border border-white/10 hover:border-lime-400/40 shadow-md transition-all duration-300 flex flex-col justify-between space-y-3 relative group"
                    >
                      <div className="space-y-2">
                        {/* Image & Quick Action Header */}
                        <div className="relative h-24 rounded-xl overflow-hidden border border-white/10">
                          <img
                            src={court.imageUrl}
                            alt={court.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/20 to-transparent" />

                          {/* Sport Badge & Rating */}
                          <div className="absolute top-2 left-2 flex items-center space-x-1.5">
                            <span className="px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider border border-white/10">
                              {sportEmoji} {court.sport}
                            </span>
                            <span className="px-1.5 py-0.5 rounded-md bg-amber-400/90 text-black text-[9px] font-black uppercase flex items-center gap-0.5 shadow">
                              <Star className="w-2.5 h-2.5 fill-black" />
                              <span>{court.rating}</span>
                            </span>
                          </div>

                          {/* Unfavorite / Remove Button */}
                          <button
                            type="button"
                            onClick={() => {
                              if (onToggleFavoriteCourt) onToggleFavoriteCourt(court.id);
                            }}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/70 hover:bg-rose-600 text-rose-400 hover:text-white backdrop-blur-md border border-white/20 transition shadow"
                            title="Remove from Saved Courts"
                          >
                            <Heart className="w-3.5 h-3.5 fill-rose-500 text-rose-500" />
                          </button>

                          {/* Active Players Overlay */}
                          <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-[10px] font-mono text-white">
                            <span className="flex items-center font-bold bg-indigo-950/80 px-1.5 py-0.5 rounded border border-white/10">
                              <Users className="w-3 h-3 mr-1 text-lime-400" />
                              {court.activePlayersNow} Active
                            </span>
                            <span className="text-lime-300 font-extrabold">{court.distanceKm} km</span>
                          </div>
                        </div>

                        {/* Title & Address */}
                        <div>
                          <h4 className="font-extrabold text-xs text-white line-clamp-1">
                            {court.name}
                          </h4>
                          <p className="text-[11px] text-indigo-200/70 font-medium flex items-center mt-0.5 truncate">
                            <MapPin className="w-3 h-3 mr-1 text-rose-400 shrink-0" />
                            <span className="truncate">{court.address}</span>
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons: Get Directions & Host */}
                      <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={(e) => handleDirectionsLink(court, e)}
                          className="py-1.5 px-2 bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-300 hover:to-blue-400 text-black font-black italic text-[10px] uppercase rounded-xl transition flex items-center justify-center space-x-1 shadow-sm"
                        >
                          <Navigation className="w-3 h-3 fill-black/20 stroke-[2.5]" />
                          <span>Directions</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => onNavigateToTab('map')}
                          className="py-1.5 px-2 bg-indigo-900/90 hover:bg-indigo-800 text-lime-400 border border-lime-400/30 font-black italic text-[10px] uppercase rounded-xl transition flex items-center justify-center space-x-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>View Map</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* LOG WORKOUT MODAL */}
      {isWorkoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-indigo-900 border border-white/20 rounded-[2.5rem] p-6 max-w-md w-full shadow-2xl space-y-5 text-white relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Dumbbell className="w-6 h-6 text-lime-400 stroke-[2.5]" />
                <h3 className="font-black italic uppercase text-lg">Log Daily Workout</h3>
              </div>
              <button
                onClick={() => setIsWorkoutModalOpen(false)}
                className="p-1.5 text-indigo-300 hover:text-white rounded-xl bg-indigo-950 border border-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-indigo-200 font-medium">
              Log today's training drill, strength work, or practice session to earn <strong className="text-lime-400">+100 XP</strong> and complete your daily goal!
            </p>

            <form onSubmit={handleSaveWorkout} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                  Workout Category
                </label>
                <select
                  value={workoutType}
                  onChange={(e) => setWorkoutType(e.target.value)}
                  className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none"
                >
                  <option value="Volleyball Spiking & Setting Drills 🏐">Volleyball Spiking & Setting Drills 🏐</option>
                  <option value="Shooting & Free Throws 🏀">Basketball Shooting & Drills 🏀</option>
                  <option value="Soccer Dribbling & Shooting Drills ⚽">Soccer Dribbling & Shooting Drills ⚽</option>
                  <option value="Pickleball Dinks & Kitchen Drills 🏓">Pickleball Dinks & Kitchen Drills 🏓</option>
                  <option value="Batting & Pitching Drills ⚾">Baseball Batting & Pitching Drills ⚾</option>
                  <option value="Softball Fielding & Pitching 🥎">Softball Fielding & Pitching 🥎</option>
                  <option value="Passing & Route Running Drills 🏈">Football Passing & Route Running Drills 🏈</option>
                  <option value="Tennis Serve & Footwork Drills 🎾">Tennis Serve & Footwork Drills 🎾</option>
                  <option value="Cardio & Speed Sprints ⚡">Cardio & Speed Sprints ⚡</option>
                  <option value="Strength & Conditioning 🏋️">Strength & Conditioning 🏋️</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={240}
                    value={workoutMinutes}
                    onChange={(e) => setWorkoutMinutes(Number(e.target.value))}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    XP Bonus
                  </label>
                  <div className="w-full bg-indigo-950/80 text-lime-400 text-xs font-black italic rounded-xl p-3 border border-lime-400/30 flex items-center justify-between">
                    <span>+100 XP</span>
                    <Sparkles className="w-4 h-4 text-lime-400" />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                  Quick Workout Notes (Optional)
                </label>
                <textarea
                  value={workoutNotes}
                  onChange={(e) => setWorkoutNotes(e.target.value)}
                  placeholder="e.g., Hit 100/120 free throws, 5x100m sprints..."
                  rows={2}
                  className="w-full bg-indigo-950 text-white placeholder-indigo-300/50 text-xs rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none resize-none font-medium"
                />
              </div>

              <div className="pt-2 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsWorkoutModalOpen(false)}
                  className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 text-xs font-black italic uppercase rounded-xl border border-white/10"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
                >
                  <Dumbbell className="w-4 h-4" />
                  <span>Log & Claim +100 XP</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE CUSTOM GOAL MODAL */}
      {isAddGoalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-indigo-900 border border-white/20 rounded-[2.5rem] p-6 max-w-md w-full shadow-2xl space-y-5 text-white relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Target className="w-6 h-6 text-rose-400 stroke-[2.5]" />
                <h3 className="font-black italic uppercase text-lg">Create Daily Goal</h3>
              </div>
              <button
                onClick={() => setIsAddGoalModalOpen(false)}
                className="p-1.5 text-indigo-300 hover:text-white rounded-xl bg-indigo-950 border border-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateCustomGoal} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                  Goal Title
                </label>
                <input
                  type="text"
                  required
                  value={customGoalTitle}
                  onChange={(e) => setCustomGoalTitle(e.target.value)}
                  placeholder="e.g. Hit 50 Free Throws or Run 3 Miles"
                  className="w-full bg-indigo-950 text-white placeholder-indigo-300/50 text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                  Goal Description
                </label>
                <input
                  type="text"
                  value={customGoalDesc}
                  onChange={(e) => setCustomGoalDesc(e.target.value)}
                  placeholder="e.g. Complete before sunset for bonus engagement XP"
                  className="w-full bg-indigo-950 text-white placeholder-indigo-300/50 text-xs rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Target Count
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={customGoalTarget}
                    onChange={(e) => setCustomGoalTarget(Number(e.target.value))}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    XP Reward
                  </label>
                  <input
                    type="number"
                    min={25}
                    max={500}
                    step={25}
                    value={customGoalXp}
                    onChange={(e) => setCustomGoalXp(Number(e.target.value))}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddGoalModalOpen(false)}
                  className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 text-xs font-black italic uppercase rounded-xl border border-white/10"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
                >
                  <Plus className="w-4 h-4 stroke-[3]" />
                  <span>Add Goal</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE SEASON GOAL MODAL */}
      {isAddSeasonGoalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-indigo-900 border border-white/20 rounded-[2.5rem] p-6 max-w-md w-full shadow-2xl space-y-5 text-white relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Target className="w-6 h-6 text-lime-400 stroke-[2.5]" />
                <h3 className="font-black italic uppercase text-lg">Define Personal Season Goal</h3>
              </div>
              <button
                onClick={() => setIsAddSeasonGoalModalOpen(false)}
                className="p-1.5 text-indigo-300 hover:text-white rounded-xl bg-indigo-950 border border-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-indigo-200 font-medium">
              Define a personal season milestone (e.g. "Reach 50 Blocks this Season"). Progress will auto-update as you log match stats!
            </p>

            <form onSubmit={handleCreateSeasonGoal} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                  Milestone Title
                </label>
                <input
                  type="text"
                  required
                  value={seasonGoalTitle}
                  onChange={(e) => setSeasonGoalTitle(e.target.value)}
                  placeholder="e.g. Reach 50 Blocks this Season"
                  className="w-full bg-indigo-950 text-white placeholder-indigo-300/50 text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Sport
                  </label>
                  <select
                    value={seasonGoalSport}
                    onChange={(e) => setSeasonGoalSport(e.target.value as SportType)}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none capitalize"
                  >
                    <option value="volleyball">Volleyball 🏐</option>
                    <option value="basketball">Basketball 🏀</option>
                    <option value="soccer">Soccer ⚽</option>
                    <option value="football">Football 🏈</option>
                    <option value="baseball">Baseball ⚾</option>
                    <option value="softball">Softball 🥎</option>
                    <option value="pickleball">Pickleball 🏓</option>
                    <option value="tennis">Tennis 🎾</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Stat Metric / Unit
                  </label>
                  <input
                    type="text"
                    required
                    value={seasonGoalMetric}
                    onChange={(e) => setSeasonGoalMetric(e.target.value)}
                    placeholder="Blocks, Kills, Points, Wins..."
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Target Milestone Count
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    value={seasonGoalTarget}
                    onChange={(e) => setSeasonGoalTarget(Number(e.target.value))}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none font-mono"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider text-indigo-200">
                    Reward XP
                  </label>
                  <input
                    type="number"
                    min={100}
                    max={2000}
                    step={50}
                    value={seasonGoalXp}
                    onChange={(e) => setSeasonGoalXp(Number(e.target.value))}
                    className="w-full bg-indigo-950 text-white text-xs font-bold rounded-xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none font-mono text-lime-400"
                  />
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddSeasonGoalModalOpen(false)}
                  className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 text-xs font-black italic uppercase rounded-xl border border-white/10"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
                >
                  <Target className="w-4 h-4 stroke-[3]" />
                  <span>Set Milestone</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FRIEND PUBLIC PROFILE PREVIEW MODAL */}
      {selectedFriendProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-gradient-to-b from-indigo-900 to-indigo-950 border border-white/20 rounded-[2.5rem] p-6 max-w-md w-full shadow-2xl space-y-5 text-white relative">
            <button
              onClick={() => setSelectedFriendProfile(null)}
              className="absolute top-5 right-5 p-1.5 text-indigo-300 hover:text-white rounded-xl bg-indigo-950 border border-white/10 z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Profile Header */}
            <div className="text-center space-y-3 pt-2">
              <div className="relative inline-block mx-auto">
                <img
                  src={selectedFriendProfile.friendAvatar}
                  alt={selectedFriendProfile.friendName}
                  className="w-20 h-20 rounded-2xl object-cover ring-4 ring-lime-400 shadow-xl"
                  referrerPolicy="no-referrer"
                />
                <span className="absolute -bottom-2 -right-2 bg-lime-400 text-black font-black italic text-xs px-2 py-0.5 rounded-lg shadow">
                  Lv.{selectedFriendProfile.level}
                </span>
              </div>

              <div>
                <h3 className="text-xl font-black italic uppercase tracking-tight text-white flex items-center justify-center gap-1">
                  <span>{selectedFriendProfile.friendName}</span>
                  <CheckCircle2 className="w-4 h-4 text-lime-400 fill-lime-400/20" />
                </h3>
                <p className="text-xs text-indigo-300 font-mono font-bold">
                  {selectedFriendProfile.friendHandle} • {selectedFriendProfile.friendRole}
                </p>
              </div>
            </div>

            {/* Friend Stats Strip */}
            <div className="grid grid-cols-3 gap-2 bg-indigo-950/80 p-3.5 rounded-2xl border border-white/10 text-center">
              <div>
                <span className="text-[10px] font-black uppercase text-indigo-300 block">Level</span>
                <span className="text-sm font-black italic text-lime-400 font-mono">{selectedFriendProfile.level}</span>
              </div>
              <div>
                <span className="text-[10px] font-black uppercase text-indigo-300 block">Wins</span>
                <span className="text-sm font-black italic text-white font-mono">{selectedFriendProfile.winCount}W</span>
              </div>
              <div>
                <span className="text-[10px] font-black uppercase text-indigo-300 block">Sport</span>
                <span className="text-xs font-black italic text-amber-300 uppercase">{selectedFriendProfile.friendSport}</span>
              </div>
            </div>

            {/* Recent Achievement Banner */}
            <div className="p-3.5 rounded-2xl bg-indigo-900/60 border border-lime-400/30 space-y-1">
              <span className="text-[10px] font-black uppercase text-lime-400 tracking-wider block">
                Recent Athlete Update ({selectedFriendProfile.timeAgo})
              </span>
              <p className="text-xs font-extrabold text-white">
                {selectedFriendProfile.actionTitle}
              </p>
              <p className="text-xs text-indigo-200/80 font-medium">
                {selectedFriendProfile.actionDetail}
              </p>
            </div>

            {/* Actions */}
            <div className="pt-2 flex items-center space-x-3">
              <button
                type="button"
                onClick={() => {
                  handleCheerFriendActivity(selectedFriendProfile.id, selectedFriendProfile.friendName);
                  setSelectedFriendProfile(null);
                }}
                className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white font-black italic uppercase text-xs rounded-xl shadow-lg shadow-rose-500/20 transition flex items-center justify-center space-x-1.5"
              >
                <Flame className="w-4 h-4 fill-white/20" />
                <span>Send High Five 🔥</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setSelectedFriendProfile(null);
                  onNavigateToTab('community');
                }}
                className="flex-1 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center justify-center space-x-1"
              >
                <Users className="w-4 h-4" />
                <span>Full Profile</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QUICK MATCH FLOATING ACTION BUTTON (FAB) */}
      {Boolean(user?.quickMatchEnabled === true || user?.isAvailableForQuickMatch === true) && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            type="button"
            onClick={() => {
              triggerHaptic('medium');
              setIsQuickMatchOpen(true);
            }}
            className="px-5 py-3.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-xs rounded-full shadow-2xl shadow-lime-400/40 border-2 border-black/30 hover:scale-105 active:scale-95 transition flex items-center space-x-2 animate-bounce cursor-pointer group"
            title="Automatically find nearest active court & check in"
          >
            <Zap className="w-5 h-5 fill-black group-hover:rotate-12 transition transform" />
            <span>Quick Match ⚡</span>
          </button>
        </div>
      )}

      {/* QUICK MATCH ONE-TAP DIALOG */}
      <QuickMatchModal
        isOpen={isQuickMatchOpen}
        onClose={() => {
          console.log('[DashboardView] QuickMatchModal onClose fired - setting isQuickMatchOpen to false');
          setIsQuickMatchOpen(false);
        }}
        courts={courts}
        pickupGames={pickupGames}
        user={user}
        onCheckInSuccess={(court) => {
          if (onEarnXp) {
            onEarnXp(100, `Quick Match One-Tap Check-In: ${court.name}`);
          }
          setQuickMatchSuccessToast(`⚡ Successfully checked in at ${court.name}! (+100 XP)`);
          setTimeout(() => setQuickMatchSuccessToast(null), 4000);
        }}
        onNavigateToTab={onNavigateToTab}
      />

      {/* Quick Match Toast Notification */}
      {quickMatchSuccessToast && (
        <div className="fixed bottom-20 right-6 z-50 bg-gradient-to-r from-emerald-500 to-lime-400 text-black font-black italic text-xs px-4 py-3 rounded-2xl shadow-2xl border border-black/30 animate-bounce flex items-center space-x-2">
          <Sparkles className="w-4 h-4 fill-black" />
          <span>{quickMatchSuccessToast}</span>
        </div>
      )}
    </div>
  );
};
